import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Modal,
  Pagination,
  Radio,
  Tag,
  Tooltip,
} from 'antd';
import {
  debounce,
  filter,
  find,
  forEach,
  isEmpty,
  map,
  toInteger,
} from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import Countdown from 'react-countdown';
import { useLocation } from 'react-router-dom';
import useInventoryUnitsFeature from '../../../../../hooks/useInventoryUnitsFeature';
import MappedUnitsCell from '../../../../../components/MappedUnitsCell';
import { AppContext } from '../../../../../AppContext';
import { ReactComponent as CommentIcon } from '../../../../../assets/svg/comment-dots-solid.svg';
import { ReactComponent as LocationIcon } from '../../../../../assets/svg/location-dot-solid.svg';
import { ReactComponent as TruckIcon } from '../../../../../assets/svg/purple_truck.svg';
import { ReactComponent as FilterIcon } from '../../../../../assets/svg/sliders-solid.svg';
import {
  BREACH_TIME_CRITICAL_THRESHOLD,
  BREACH_TIME_WARNING_THRESHOLD,
  CANCELLATION_TYPE,
  CRITICAL_ORDERS_KEY,
  DATE_FORMATS,
  DELIVERY_TYPE,
  DELIVERY_TYPE_OPTIONS,
  DISABLE_ORDER,
  DISABLE_ORDER_ITEM,
  EXPRESS_DELIVERY_EMOJI,
  EXPRESS_DELIVERY_TOOLTIP_TEXT,
  FULFILLMENT_STATUS,
  LIBRARY_ALL_DELIVERY_TABLE_ACTIONS_OPTIONS,
  LIMIT,
  NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS,
  NEW_ORDER_TABLE_ACTIONS_OPTIONS,
  ORDER_STATUS,
  ORDER_TAG_TYPES,
  ORDER_TYPE,
  ORDER_TYPE_FILTER_OPTIONS,
  ORDERS_POLLING_INTERVAL,
  QUICK_DELIVERY_EMOJI,
  QUICK_DELIVERY_TOOLTIP_TEXT,
  ROUTES,
  SORT,
  SORTING,
  STOCK_COLUMN_TOOLTIPS,
  TRANSFER_ORDER_STATUS,
  USER_ROLES,
} from '../../../../../common/constants';
import {
  formatDate,
  getAttachmentSource,
  getRowClassName,
  hasNonEmptyValueObj,
  hasPreOrderToy,
  renderer,
  shouldNotifyForNewOrders,
  humanizeEnum,
} from '../../../../../common/utils';
import ChallanDownloadButton from '../../../../../components/ChallanDownloadButton';
import ColumnTitleWithInfo from '../../../../../components/ColumnTitleWithInfo';
import CommonPreview from '../../../../../components/CommonPreview';
import CommonPreviewTable from '../../../../../components/CommonPreviewTable';
import CommonTable from '../../../../../components/CommonTable';
import FulfilmentPossibilityDrawer from '../../../../../components/FulfilmentPossibilityDrawer';
import LoaderComponent from '../../../../../components/LoaderComponent';
import OrderDynamicTags from '../../../../../components/OrderDynamicTags';
import OrderStatusSelect from '../../../../../components/OrderStatusSelect';
import OrderTagsFilter from '../../../../../components/OrderTagsFilter';
import RenderCopyIcon from '../../../../../components/RenderCopyIcon';
import SearchComponent from '../../../../../components/SearchComponent';
import SelectComponent from '../../../../../components/SelectComponent';
import UnitScanModal from '../../../../../components/UnitScan/UnitScanModal';
import { UNIT_SCAN_MODES } from '../../../../../components/UnitScan/constants';
import ShowPreOrderSwitch from '../../../../../components/ShowPreOrderSwitch';
import ShowDelayedOrdersSwitch from '../../../../../components/ShowDelayedOrdersSwitch';
import SortDropdown from '../../../../../components/SortDropdown';
import TrackOrderIframeModal from '../../../../../components/TrackOrderIframeModal';
import { PINCODES } from '../../../../../components/graphql/Queries';
import useInfiniteScroll from '../../../../../hooks/useInfiniteScroll';
import useOrderAlert from '../../../../../hooks/useOrderAlert';
import ContentMasterModal from '../../../../../modules/orders/modules/all/components/ContentMasterModal';
import DelayReasonModal from '../../../../../modules/orders/modules/all/components/DelayReasonModal';
import NoteModal from '../../../../../modules/orders/modules/all/components/NoteModal';
import RiderLocationModal from '../../../../../modules/orders/modules/all/components/RiderLocationModal';
import BigToyIcon from '../../../../../modules/orders/modules/new/components/BigToyIcon';
import StatusFlag from '../../../../../modules/orders/modules/new/components/OrderTagRenderer';
import { UPDATE_ORDER_METADATA } from '../../../../../modules/orders/modules/new/graphql/Mutations';
import { USER_LAT_LONG_DETAILS } from '../../../../../modules/users/graphql/Queries';
import { USERS } from '../../../../raiseRequest/graphql/Queries';
import {
  CANCEL_ORDER_LIBRARY,
  RESET_ORDER_CHALLAN,
  RESET_ORDER_ITEM_STICKER,
  UPDATE_ORDER_STATUS_LIBRARY,
} from '../graphql/Mutations';
import {
  FULFILMENT_POSSIBILITY_ORDERS,
  GET_ORDER_CHALLAN_DOWNLOAD_URL,
  GET_ORDER_STICKER_DOWNLOAD_URL,
  ORDER_ITEMS,
  ORDERS,
  ORDERS_SELECT,
} from '../graphql/Queries';
import AddCommentModal from './AddCommentModal';
import AddDelayReasonModal from './AddDelayReasonModal';
import AllFilterOrderModal from './AllFilterOrderModal';
import AllOrderItemModal from './AllOrderItemModal';
import AllOrderList from './AllOrderList';
import createOrderItemStaffColumns from '../../../../../components/OrderItemStaff/createOrderItemStaffColumns';
import BulkAssignStaffButton from '../../../../../components/OrderItemStaff/BulkAssignStaffButton';
import useNestedOrderItemSelection from '../../../../../components/OrderItemStaff/useNestedOrderItemSelection';
import { isReassignableOrderItem } from '../../../../../components/OrderItemStaff/constants';
import {
  patchNestedOrderItemStaff,
  patchOrderItemStaff,
} from '../../../../../components/OrderItemStaff/utils';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialNewDeliveryFilter = {
  limit: LIMIT,
  search: '',
  skip: 0,
};

const defaultInitialNewDeliverySort = {
  field: 'createdAt',
  order: 'DESC',
};

const initialTableFilter = {
  skip: 0,
  limit: LIMIT,
  search: '',
};

const initialTableSort = {
  field: 'createdAt',
  order: 'DESC',
};

let filterScrollDebounceJob = null;

const sort = [
  {
    label: SORTING?.ORDER_ID_A_Z?.label,
    value: SORTING?.ORDER_ID_A_Z?.value,
    field: SORTING?.ORDER_ID_A_Z?.field,
    sort: SORTING?.ORDER_ID_A_Z?.sort,
  },
  {
    label: SORTING?.ORDER_ID_Z_A?.label,
    value: SORTING?.ORDER_ID_Z_A?.value,
    field: SORTING?.ORDER_ID_Z_A?.field,
    sort: SORTING?.ORDER_ID_Z_A?.sort,
  },
  {
    label: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.label,
    value: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.value,
    field: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.field,
    sort: SORTING?.TOTAL_ORDERS_COUNT_A_Z?.sort,
  },
  {
    label: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.label,
    value: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.value,
    field: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.field,
    sort: SORTING?.TOTAL_ORDERS_COUNT_Z_A?.sort,
  },
];

const AllDeliveryTable = ({
  exportFilter,
  setExportFilter,
  handleSelectOrders,
  selectedOrders,
  setSelectedOrders,
  isTruckIconVisible = true,
  isFulfilmentPossibility = false,
  staffSyncKey = 0,
}) => {
  const initialNewDeliverySort = isFulfilmentPossibility
    ? { field: 'expectedDeliveryDate', order: SORT?.ASC }
    : defaultInitialNewDeliverySort;

  const location = useLocation();
  const [form] = Form.useForm();
  const [delayForm] = Form.useForm();
  const {
    dispatch,
    state: { isDesktop, orderCount },
  } = useContext(AppContext);
  const queryParams = new URLSearchParams(location?.search);
  const status = queryParams?.get('status') || '';
  const { isUnitsFeatureEnabled } = useInventoryUnitsFeature();

  const [orderList, setOrderList] = useState([]);
  const [possibilityDrawerVisible, setPossibilityDrawerVisible] = useState(
    false,
  );
  const [selectedPossibilityItem, setSelectedPossibilityItem] = useState(null);
  const isPollingRef = useRef(false);
  const processedOrderIdsRef = useRef(new Set());
  const isAutoRefreshRef = useRef(false);
  const isLatestOrdersRequestAutoRef = useRef(false);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const orderSearchTermRef = useRef('');
  const [isEmptyOrderList, setIsEmptyOrderList] = useState(false);
  const [order, setOrder] = useState({});
  const [orderItem, setOrderItem] = useState({});
  const [sortedInfo, setSortedInfo] = useState(
    isFulfilmentPossibility
      ? {
          field: 'expectedDeliveryDate',
          columnKey: 'expectedDeliveryDate',
          order: 'ascend',
        }
      : {},
  );
  const sortedInfoRef = useRef(
    isFulfilmentPossibility
      ? {
          field: 'expectedDeliveryDate',
          columnKey: 'expectedDeliveryDate',
          order: 'ascend',
        }
      : null,
  );
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const paginationPropRef = useRef(initialPaginationValue);
  const [isOrderLoading, setIsOrderLoading] = useState(true);
  const [isNotePrompts, setIsNotePrompts] = useState(false);
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isAddCommentLoading, setIsAddCommentLoading] = useState(false);
  const [isOrderAction, setIsOrderAction] = useState(false);
  const [isFulfillLoading, setIsFulfillLoading] = useState(false);
  const [isFulfillPrompts, setIsFulfillPrompts] = useState(false);
  const [isUnitScanOpen, setIsUnitScanOpen] = useState(false);
  const [isReadyLoading, setIsReadyLoading] = useState(false);
  const [isReadyPrompts, setIsReadyPrompts] = useState(false);
  const [isChallanLoading, setIsChallanLoading] = useState({});
  const [isResetLoading, setIsResetLoading] = useState({});
  const [isStickerLoading, setIsStickerLoading] = useState({});
  const [isResetStickerLoading, setIsResetStickerLoading] = useState({});
  const [isReasonPrompt, setIsReasonPrompt] = useState(false);
  const [isDelayReasonPrompts, setIsDelayReasonPrompts] = useState(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [nestedData, setNestedData] = useState({});
  const getStaffColumns = createOrderItemStaffColumns({
    isLibrary: true,
    canEdit: location?.pathname?.includes(ROUTES?.NEW),
    onReassigned: (...args) => {
      setOrderList((prev) => patchOrderItemStaff(prev, ...args));
      setNestedData((prev) =>
        patchNestedOrderItemStaff(prev, 'orderItems', ...args),
      );
    },
  });
  const [isLoading, setIsLoading] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const finalWhereFiltersRef = useRef(finalWhereFilters);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  // Fulfilment-possibility rows are order items, so they're selected directly.
  const [staffSelectedKeys, setStaffSelectedKeys] = useState([]);
  const [staffSelectedRows, setStaffSelectedRows] = useState([]);

  const {
    hasItemSelection,
    clearItemSelection,
    getNestedRowSelection,
    getSelectedOrderItems,
  } = useNestedOrderItemSelection();
  const selectedTagIdsRef = useRef([]);
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const isNewPath = location?.pathname?.includes(ROUTES?.NEW);
  const isAllDeliveryTab = isDesktop && !isNewPath && !isFulfilmentPossibility;
  const [visible, setVisible] = useState(false);
  const [toyImages, setToyImages] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [toyPreviewImage, setToyPreviewImage] = useState();

  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [isFetchMoreOrderLoading, setIsFetchMoreOrderLoading] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isAllOrderItemModalOpen, setIsAllOrderItemModalOpen] = useState(false);
  const [isAllSortPopoverOpen, setIsAllSortPopoverOpen] = useState(false);
  const [isContentMasterModalOpen, setIsContentMasterModalOpen] = useState(
    false,
  );
  const [thresholdData, setThresholdData] = useState({});
  const statusFilter = isNewPath ? [ORDER_STATUS?.PLACED?.value] : undefined;
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [refreshIntervalId, setRefreshIntervalId] = useState(null);
  const [trackingUrl, setTrackingUrl] = useState('');
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const {
    playNotificationSound,
    playNotificationSoundForNewOrder,
  } = useOrderAlert();
  const criticalOrdersRef = useRef(new Set());
  const orderId = useRef(queryParams?.get('orderId') || '');
  const previousCountRef = useRef(0);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    const stored = localStorage.getItem(CRITICAL_ORDERS_KEY);
    if (stored) {
      try {
        criticalOrdersRef.current = new Set(JSON.parse(stored));
      } catch (error) {
        criticalOrdersRef.current = new Set();
      }
    }
  }, []);

  const [USERS_LAT_LONG_DETAILS] = useLazyQuery(USER_LAT_LONG_DETAILS, {
    onCompleted: (response) => {
      setLatitude(response?.user?.latLong?.coordinates[1]);
      setLongitude(response?.user?.latLong?.coordinates[0]);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const openGoogleMaps = (lat, lng, userid) => {
    setIsLocationModalVisible(true);
    setLatitude(lat);
    setLongitude(lng);
    USERS_LAT_LONG_DETAILS({
      variables: { where: { id: userid } },
    });
    const intervalId = setInterval(() => {
      USERS_LAT_LONG_DETAILS({
        variables: { where: { id: userid } },
      });
    }, 30000);

    setRefreshIntervalId(intervalId);
  };
  useEffect(() => {
    if (!isLocationModalVisible && refreshIntervalId) {
      clearInterval(refreshIntervalId);
      setRefreshIntervalId(null);
    }
  }, [isLocationModalVisible, refreshIntervalId]);

  useEffect(
    () => () => {
      if (refreshIntervalId) {
        clearInterval(refreshIntervalId);
      }
    },
    [],
  );

  const tempComment = useRef(null);
  const isLastItem = (orderIds) => {
    if (isFulfilmentPossibility) return true;
    const orderData = nestedData?.[orderIds]?.orderItems?.data || [];
    const processedCount = orderData?.filter(
      (item) =>
        item?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
        item?.status === FULFILLMENT_STATUS?.CANCELLED?.value,
    )?.length;

    const totalItems = orderData?.length;

    return processedCount + 1 >= totalItems;
  };

  const updateMainOrderList = (orderIds, commentData) => {
    setOrder((prev) => ({
      ...prev,
      metadata: {
        ...commentData,
      },
    }));
    setOrderList((prev) =>
      prev?.map((orders) => {
        if (orders?.id === orderIds) {
          return {
            ...orders,
            metadata: {
              ...commentData,
            },
          };
        }
        return orders;
      }),
    );
  };
  const tempUnfulfilledReason = useRef(null);
  const updateNestedDataLocally = (orderIds, orderItemId, newStatus) => {
    setNestedData((prev) => ({
      ...prev,
      [orderIds]: {
        ...prev?.[orderIds],
        orderItems: {
          ...prev?.[orderIds]?.orderItems,
          data:
            prev?.[orderIds]?.orderItems?.data?.map((item) =>
              item?.id === orderItemId
                ? {
                    ...item,
                    status: newStatus,
                    cancellationData:
                      newStatus === FULFILLMENT_STATUS.UNFULFILLED?.value
                        ? { ...tempUnfulfilledReason.current }
                        : null,
                  }
                : item,
            ) || [],
        },
      },
    }));
  };

  const renderBlinkingDotEl = (orderStatus) => {
    if (isNewPath && orderStatus === ORDER_STATUS.PLACED.value) {
      return (
        <Badge status="processing" color="red" className="custom-badge mr-8" />
      );
    }
    return null;
  };
  const [updateOrderMetadata] = useMutation(UPDATE_ORDER_METADATA, {
    onError: () => {},
  });
  const [runOrdersQuery] = useLazyQuery(
    isFulfilmentPossibility ? FULFILMENT_POSSIBILITY_ORDERS : ORDERS,
    {
      onCompleted: (response) => {
        const criticalThreshold = toInteger(
          response?.orders?.configs?.find(
            (config) => config?.key === BREACH_TIME_CRITICAL_THRESHOLD,
          )?.value,
        );
        const warningThreshold = toInteger(
          response?.orders?.configs?.find(
            (config) => config?.key === BREACH_TIME_WARNING_THRESHOLD,
          )?.value,
        );

        if (isFulfilmentPossibility) {
          dispatch({
            type: 'SET_ORDER_COUNT',
            data: {
              ...orderCount,
              fulfilmentPossibilityOrderCount: response?.orders?.count,
            },
          });
        } else if (isNewPath) {
          dispatch({
            type: 'SET_ORDER_COUNT',
            data: {
              ...orderCount,
              newOrderCount: response?.orders?.count,
              placedOrderCount: response?.orders?.count,
            },
          });
        }
        setThresholdData({
          criticalThreshold,
          warningThreshold,
        });

        const newOrders = response?.orders?.data || [];
        const existingIds = new Set(
          orderList?.map((currentOrder) => currentOrder?.orderNumber),
        );
        const uniqueNewOrders = newOrders?.filter(
          (currentOrder) => !existingIds?.has(currentOrder.orderNumber),
        );

        const shouldNotify = shouldNotifyForNewOrders(
          uniqueNewOrders,
          orderList,
          isPollingRef,
          isInitialMountRef,
          finalWhereFilters,
          orderSearchTerm,
          previousCountRef,
        );

        if (shouldNotify && isNewPath) {
          playNotificationSoundForNewOrder(
            uniqueNewOrders,
            paginationProp?.current > 1,
          );
        }

        previousCountRef.current = response?.orders?.count;
        isInitialMountRef.current = false;
        const isAutoRefresh = isLatestOrdersRequestAutoRef.current;

        if (isDesktop) {
          const freshOrders = response?.orders?.data || [];
          if (isAutoRefresh && processedOrderIdsRef.current.size) {
            const freshIds = new Set(map(freshOrders, 'id'));
            setOrderList((prev) => {
              const mergedOrders = [...freshOrders];
              prev?.forEach((prevOrder, index) => {
                if (
                  processedOrderIdsRef.current.has(prevOrder?.id) &&
                  !freshIds.has(prevOrder?.id)
                ) {
                  mergedOrders.splice(Math.min(index, mergedOrders.length), 0, {
                    ...prevOrder,
                    isRetainedAfterProcess: true,
                  });
                }
              });
              return mergedOrders;
            });
          } else {
            setOrderList([...freshOrders]);
          }
        } else {
          // eslint-disable-next-line no-lonely-if
          if (isPollingRef?.current) {
            setOrderList([...uniqueNewOrders, ...orderList]);
            isPollingRef.current = false;
          } else {
            setOrderList([...orderList, ...uniqueNewOrders]);
          }
        }
        if (
          response?.orders?.count === 0 &&
          initialPaginationValue?.total === 0
        ) {
          setIsEmptyOrderList(true);
        } else {
          setIsEmptyOrderList(false);
        }
        if (response?.orders?.data?.length < LIMIT) {
          setIsActive(false);
        }
        const pagination = {
          ...paginationProp,
          defaultPageSize: LIMIT,
          total: response?.orders?.count,
        };
        setPaginationProp(pagination);
        paginationPropRef.current = pagination;
        setIsOrderLoading(false);
        setIsFetchMoreOrderLoading(false);
      },
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const orders = (options) => {
    isLatestOrdersRequestAutoRef.current = isAutoRefreshRef.current;
    if (!isAutoRefreshRef.current) processedOrderIdsRef.current = new Set();
    isAutoRefreshRef.current = false;
    return runOrdersQuery(options);
  };

  const [orderItems] = useLazyQuery(ORDER_ITEMS, {
    fetchPolicy: 'no-cache',
    onError() {},
  });

  const [singleOrder] = useLazyQuery(ORDERS, {
    fetchPolicy: 'no-cache',
    onError() {},
  });

  const refreshOrderInPlace = async (processedOrderId) => {
    processedOrderIdsRef.current.add(processedOrderId);
    const response = await singleOrder({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: 1,
          orderType: ORDER_TYPE?.ALL,
          orderIds: [processedOrderId],
        },
        sort: defaultInitialNewDeliverySort,
      },
    });
    const updatedOrder = response?.data?.orders?.data?.[0];
    if (!updatedOrder) return;
    const activeStatusFilter = finalWhereFiltersRef?.current?.status;
    const isRetainedAfterProcess =
      activeStatusFilter?.length > 0 &&
      !activeStatusFilter?.includes(updatedOrder?.status);
    setOrderList((prev) =>
      prev?.map((item) =>
        item?.id === updatedOrder?.id
          ? { ...item, ...updatedOrder, isRetainedAfterProcess }
          : item,
      ),
    );
    setOrder((prev) =>
      prev?.id === updatedOrder?.id ? { ...prev, ...updatedOrder } : prev,
    );
  };

  const [getOrderChallanDownloadUrl] = useLazyQuery(
    GET_ORDER_CHALLAN_DOWNLOAD_URL,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [getOrderStickerDownloadUrl] = useLazyQuery(
    GET_ORDER_STICKER_DOWNLOAD_URL,
    {
      fetchPolicy: 'network-only',
      onError() {},
    },
  );

  const [users, { loading: isUsersLoading }] = useLazyQuery(USERS, {
    onCompleted: (response) => {
      if (response?.users?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.users?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.users?.data, (item) =>
        optionsCopy?.push({
          label: `${
            item?.firstName || item?.lastName
              ? `${item?.firstName || ''} ${item?.lastName || ''}`
              : '-'
          }`,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [pincodes, { loading: isPincodesLoading }] = useLazyQuery(PINCODES, {
    onCompleted: (response) => {
      if (response?.pincodes?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }
      if (response?.pincodes?.count === 0) {
        setIsEmptyFilterList(true);
      } else {
        setIsEmptyFilterList(false);
      }
      const optionsCopy = [];
      forEach(response?.pincodes?.data, (item) =>
        optionsCopy?.push({
          label: item?.pincode,
          key: item?.id,
        }),
      );
      setFilterList([...filterList, ...optionsCopy]);
      setFilterLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {
      setFilterLoading(false);
    },
  });

  const [orderSelect, { loading: isOrderSelectLoading }] = useLazyQuery(
    ORDERS_SELECT,
    {
      onCompleted: (response) => {
        if (response?.orders?.data?.length < LIMIT) {
          setFilterIsEnd(true);
        }
        if (response?.orders?.count === 0) {
          setIsEmptyFilterList(true);
        } else {
          setIsEmptyFilterList(false);
        }
        const optionsCopy = [];
        forEach(response?.orders?.data, (item) =>
          optionsCopy?.push({
            label: item?.orderNumber,
            key: item?.id,
          }),
        );
        setFilterList([...filterList, ...optionsCopy]);
        setFilterLoading(false);
      },
      fetchPolicy: 'network-only',
      onError() {
        setFilterLoading(false);
      },
    },
  );

  const [cancelOrderLibrary] = useMutation(CANCEL_ORDER_LIBRARY, {
    onError: () => {},
  });

  const [updateOrderStatusLibrary] = useMutation(UPDATE_ORDER_STATUS_LIBRARY, {
    onError: () => {},
  });

  const [resetOrderChallan] = useMutation(RESET_ORDER_CHALLAN, {
    onError: () => {},
  });

  const [resetOrderItemSticker] = useMutation(RESET_ORDER_ITEM_STICKER, {
    onError: () => {},
  });

  const fetchOrders = () => {
    if (orderId?.current && isNewPath) {
      setOrderSearchTerm(orderId?.current);
      orderSearchTermRef.current = orderId?.current;
    }

    const { text, value } = find(map(ORDER_STATUS), { value: status }) || {};

    if (value) {
      const filterValue = { status: [value] };
      const filterTagValue = { status: [{ label: text, key: value }] };
      setFinalWhereFilters(filterValue);
      finalWhereFiltersRef.current = filterValue;
      setWhereFilter(filterValue);
      setFilterTags(filterTagValue);
      setFinalFilterTags(filterTagValue);
    }

    isPollingRef.current = true;
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip: isDesktop
            ? (paginationPropRef?.current?.current - 1) *
              (paginationPropRef?.current?.pageSize || 0)
            : 0,
          search: orderSearchTermRef?.current,
          limit: paginationPropRef?.current?.pageSize || LIMIT,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          orderIds: finalWhereFiltersRef?.current?.orderNumber,
          deliveryType: finalWhereFiltersRef?.current?.deliveryType,
          status: isNewPath
            ? statusFilter
            : finalWhereFiltersRef?.current?.status,
          orderTypes:
            finalWhereFiltersRef?.current?.orderTypes?.length > 0
              ? finalWhereFiltersRef?.current?.orderTypes
              : undefined,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFiltersRef?.current?.deliveredOn?.from?.$d,
            to: finalWhereFiltersRef?.current?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
                      ?.$d,
                  to:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFiltersRef?.current?.pincodes,
          statusTiming: finalWhereFiltersRef?.current?.statusTiming,
          isPreOrder: finalWhereFiltersRef?.current?.isPreOrder,
          isDelayed: finalWhereFiltersRef?.current?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  useEffect(() => {
    fetchOrders();
    const intervalId = setInterval(() => {
      isAutoRefreshRef.current = true;
      fetchOrders();
    }, ORDERS_POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, []);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    isInitialMountRef.current = true;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    paginationPropRef.current = { ...paginationProp, pagination };
    setIsOrderLoading(true);
    setSortedInfo(sorter);
    sortedInfoRef.current = sorter;
    setNestedData({});
    setExpandedRowKeys([]);
    if (!isNewPath) {
      setSelectedOrders([]);
    }
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          skip,
          limit: pagination?.pageSize,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFilters?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const [, setCurrentTime] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    orderId.current = trimValue;
    setOrderSearchTerm(trimValue);
    orderSearchTermRef.current = trimValue;
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    setIsOrderLoading(true);
    setOrderList([]);
    setNestedData({});
    setExpandedRowKeys([]);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          orderIds: finalWhereFilters?.orderNumber,
          search: trimValue,
          deliveryType: finalWhereFilters?.deliveryType,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFilters?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setExportFilter({ ...exportFilter, search: trimValue });
  };

  const handleAddCommentInOrder = async (values) => {
    setIsAddCommentLoading(true);
    const response = await cancelOrderLibrary({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsAddCommentModalOpen(false);
      setNestedData({});
      setExpandedRowKeys([]);
      setOrderList([]);
      form?.resetFields();
      orders({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            pincodeIds: finalWhereFilters?.pincodes,
            statusTiming: finalWhereFilters?.statusTiming,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsAddCommentLoading(false);
  };

  const handleAddCommentInOrderItem = async (values) => {
    setIsAddCommentLoading(true);
    const response = await updateOrderStatusLibrary({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          additionalComment: values?.comment?.trim(),
          reason: values?.reason,
          status: FULFILLMENT_STATUS?.UNFULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      setIsAddCommentModalOpen(false);
      if (!isAllDeliveryTab && isLastItem(order?.id)) {
        setIsAllOrderItemModalOpen(false);
        setIsOrderLoading(true);
        setNestedData({});
        setExpandedRowKeys([]);
        setOrderList([]);
        form?.resetFields();
        orders({
          variables: {
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
              orderIds: finalWhereFilters?.orderNumber,
              search: orderSearchTerm,
              deliveryType: finalWhereFilters?.deliveryType,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              orderTypes:
                finalWhereFilters?.orderTypes?.length > 0
                  ? finalWhereFilters?.orderTypes
                  : undefined,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: finalWhereFilters?.deliveredOn?.from?.$d,
                to: finalWhereFilters?.deliveredOn?.to?.$d,
              },
              ...(finalWhereFilters?.expectedDeliveryDate?.from
                ? {
                    expectedDeliveryDate: {
                      from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                      to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                    },
                  }
                : {}),
              pincodeIds: finalWhereFilters?.pincodes,
              statusTiming: finalWhereFilters?.statusTiming,
              isPreOrder: finalWhereFilters?.isPreOrder,
              isDelayed: finalWhereFilters?.isDelayed,
              orderTagIds:
                selectedTagIdsRef?.current?.length > 0
                  ? selectedTagIdsRef.current
                  : undefined,
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        tempUnfulfilledReason.current = {
          reason: values?.reason,
          additionalComment: values?.comment?.trim(),
          type: CANCELLATION_TYPE?.LIBRARY?.value,
          date: Date(),
        };

        const isOrderProcessed = isLastItem(order?.id);
        updateNestedDataLocally(
          order?.id,
          orderItem?.id,
          FULFILLMENT_STATUS?.UNFULFILLED?.value,
        );

        if (isOrderProcessed) {
          form?.resetFields();
          refreshOrderInPlace(order?.id);
        }
      }
    }
    setIsAddCommentLoading(false);
  };
  const handleFulfillOrderItem = async () => {
    setIsFulfillLoading(true);
    const response = await updateOrderStatusLibrary({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          orderItemId: orderItem?.id,
          status: FULFILLMENT_STATUS?.FULFILLED?.value,
        },
      },
    });
    if (response?.data) {
      if (!isAllDeliveryTab && isLastItem(order?.id)) {
        setIsAllOrderItemModalOpen(false);
        setIsOrderLoading(true);
        setIsFulfillPrompts(false);
        setNestedData({});
        setOrderList([]);
        setExpandedRowKeys([]);
        orders({
          variables: {
            filter: {
              ...initialNewDeliveryFilter,
              skip:
                (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
              limit: paginationProp?.pageSize || LIMIT,
              orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
              orderIds: finalWhereFilters?.orderNumber,
              search: orderSearchTerm,
              deliveryType: finalWhereFilters?.deliveryType,
              status:
                finalWhereFilters?.status?.length > 0
                  ? finalWhereFilters?.status
                  : statusFilter,
              orderTypes:
                finalWhereFilters?.orderTypes?.length > 0
                  ? finalWhereFilters?.orderTypes
                  : undefined,
              dateRange: {
                from: finalWhereFilters?.createdAt?.from?.$d,
                to: finalWhereFilters?.createdAt?.to?.$d,
              },
              deliveredOn: {
                from: finalWhereFilters?.deliveredOn?.from?.$d,
                to: finalWhereFilters?.deliveredOn?.to?.$d,
              },
              ...(finalWhereFilters?.expectedDeliveryDate?.from
                ? {
                    expectedDeliveryDate: {
                      from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                      to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                    },
                  }
                : {}),
              pincodeIds: finalWhereFilters?.pincodes,
              statusTiming: finalWhereFilters?.statusTiming,
              isPreOrder: finalWhereFilters?.isPreOrder,
              isDelayed: finalWhereFilters?.isDelayed,
              orderTagIds:
                selectedTagIdsRef?.current?.length > 0
                  ? selectedTagIdsRef.current
                  : undefined,
            },
            sort: sortedInfo?.column
              ? {
                  field: sortedInfo?.field,
                  order:
                    sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
                }
              : initialNewDeliverySort,
          },
        });
      } else {
        const isOrderProcessed = isLastItem(order?.id);
        updateNestedDataLocally(
          order.id,
          orderItem.id,
          FULFILLMENT_STATUS?.FULFILLED?.value,
        );

        if (tempComment?.current?.[order?.id])
          updateMainOrderList(order?.id, tempComment?.current?.[order?.id]);

        if (isOrderProcessed) refreshOrderInPlace(order?.id);
      }
      setIsDelayReasonPrompts(false);
      delayForm?.resetFields();
      setIsReadyLoading(false);
    }
    setIsFulfillLoading(false);
  };

  const handleReadyOrder = async () => {
    setIsAllOrderItemModalOpen(false);
    setIsReadyLoading(true);
    const response = await updateOrderStatusLibrary({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          status: ORDER_STATUS?.CONFIRMED?.value,
        },
      },
    });
    if (response?.data) {
      setIsOrderLoading(true);
      setIsReadyPrompts(false);
      setNestedData({});
      setOrderList([]);
      setExpandedRowKeys([]);
      orders({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip:
              (paginationProp?.current - 1) * (paginationProp?.pageSize || 0),
            limit: paginationProp?.pageSize || LIMIT,
            orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            pincodeIds: finalWhereFilters?.pincodes,
            statusTiming: finalWhereFilters?.statusTiming,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
    setIsDelayReasonPrompts(false);
    delayForm?.resetFields();
    setIsReadyLoading(false);
  };

  const handleAddDelayReason = async (values) => {
    setIsReadyLoading(true);
    const metadataResponse = await updateOrderMetadata({
      variables: {
        where: {
          id: order?.id,
        },
        data: {
          metadata: [
            { key: 'delayReason', value: values?.reason },
            { key: 'delayNote', value: values?.comment },
          ],
        },
      },
    });
    if (metadataResponse?.data) {
      tempComment.current = {
        [order?.id]: {
          delayReason: { value: values?.reason },
          delayNote: { value: values?.comment },
        },
      };
      if (isOrderAction) {
        handleReadyOrder([order?.id]);
      } else {
        handleFulfillOrderItem();
      }
    } else {
      setIsReadyLoading(false);
    }
  };

  const handleFilter = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    setIsOrderLoading(true);
    setIsAllFilterModalOpen(false);
    setOrderList([]);
    setIsActive(true);
    const filters = {
      status: isNewPath ? statusFilter : values?.status,
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      deliveredOn: values?.deliveredOn?.[1]
        ? { from: values?.deliveredOn?.[0], to: values?.deliveredOn?.[1] }
        : null,
      pincodes: values?.pincodes,
      deliveryType: values?.deliveryType,
      orderTypes: finalWhereFilters?.orderTypes,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    finalWhereFiltersRef.current = filters;
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          search: orderSearchTerm,
          deliveryType: values?.deliveryType,
          status: values?.status?.length > 0 ? values?.status : statusFilter,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          dateRange: {
            from: values?.createdAt?.[0],
            to: values?.createdAt?.[1],
          },
          deliveredOn: {
            from: values?.deliveredOn?.[0],
            to: values?.deliveredOn?.[1],
          },
          pincodeIds: values?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const handleTagFilterChange = (tagIds) => {
    selectedTagIdsRef.current = tagIds;
    setSelectedTagIds(tagIds);
    setIsOrderLoading(true);
    setOrderList([]);
    setIsActive(true);
    setNestedData({});
    setExpandedRowKeys([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = {
      ...paginationPropRef.current,
      current: 1,
      skip: 0,
    };
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          orderIds: finalWhereFiltersRef?.current?.orderNumber,
          search: orderSearchTermRef?.current,
          deliveryType: finalWhereFiltersRef?.current?.deliveryType,
          status: isNewPath
            ? statusFilter
            : finalWhereFiltersRef?.current?.status,
          orderTypes:
            finalWhereFiltersRef?.current?.orderTypes?.length > 0
              ? finalWhereFiltersRef?.current?.orderTypes
              : undefined,
          dateRange: {
            from: finalWhereFiltersRef?.current?.createdAt?.from?.$d,
            to: finalWhereFiltersRef?.current?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFiltersRef?.current?.deliveredOn?.from?.$d,
            to: finalWhereFiltersRef?.current?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.from
                      ?.$d,
                  to:
                    finalWhereFiltersRef?.current?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFiltersRef?.current?.pincodes,
          statusTiming: finalWhereFiltersRef?.current?.statusTiming,
          isPreOrder: finalWhereFiltersRef?.current?.isPreOrder,
          isDelayed: finalWhereFiltersRef?.current?.isDelayed,
          orderTagIds: tagIds?.length > 0 ? tagIds : undefined,
        },
        sort: sortedInfoRef?.current?.column
          ? {
              field: sortedInfoRef?.current?.field,
              order:
                sortedInfoRef?.current?.order === 'ascend'
                  ? SORT?.ASC
                  : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
  };

  const handleOrderScroll = () => {
    if (!isFetchMoreOrderLoading && !isOrderLoading) {
      isPollingRef.current = false;
      const currentLength = orderList?.length;
      setIsFetchMoreOrderLoading(true);
      orders({
        variables: {
          filter: {
            ...initialNewDeliveryFilter,
            skip: currentLength,
            limit: paginationProp?.pageSize || LIMIT,
            orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
            orderIds: finalWhereFilters?.orderNumber,
            search: orderSearchTerm,
            deliveryType: finalWhereFilters?.deliveryType,
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : statusFilter,
            orderTypes:
              finalWhereFilters?.orderTypes?.length > 0
                ? finalWhereFilters?.orderTypes
                : undefined,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            deliveredOn: {
              from: finalWhereFilters?.deliveredOn?.from?.$d,
              to: finalWhereFilters?.deliveredOn?.to?.$d,
            },
            ...(finalWhereFilters?.expectedDeliveryDate?.from
              ? {
                  expectedDeliveryDate: {
                    from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                    to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                  },
                }
              : {}),
            pincodeIds: finalWhereFilters?.pincodes,
            statusTiming: finalWhereFilters?.statusTiming,
            isPreOrder: finalWhereFilters?.isPreOrder,
            isDelayed: finalWhereFilters?.isDelayed,
            orderTagIds:
              selectedTagIdsRef?.current?.length > 0
                ? selectedTagIdsRef.current
                : undefined,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialNewDeliverySort,
        },
      });
    }
  };

  const handleSort = (values) => {
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    paginationPropRef.current = { ...paginationProp, current: 1, skip: 0 };
    setIsOrderLoading(true);
    setIsAllSortPopoverOpen(false);
    setIsActive(true);
    setOrderList([]);
    const filteredSort = sort?.filter((item) => item?.value === values?.sort);
    if (filteredSort?.[0]?.field) {
      setSortedInfo({
        ...sortedInfo,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      });

      sortedInfoRef.current = {
        ...sortedInfoRef.current,
        field: filteredSort?.[0]?.field,
        columnKey: filteredSort?.[0]?.field,
        order: filteredSort?.[0]?.sort === SORT?.ASC ? 'ascend' : 'descend',
        column: filteredSort?.[0]?.field,
      };
    }
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          orderIds: finalWhereFilters?.orderNumber,
          search: orderSearchTerm,
          deliveryType: finalWhereFilters?.deliveryType,
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : statusFilter,
          orderTypes:
            finalWhereFilters?.orderTypes?.length > 0
              ? finalWhereFilters?.orderTypes
              : undefined,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: finalWhereFilters?.deliveredOn?.from?.$d,
            to: finalWhereFilters?.deliveredOn?.to?.$d,
          },
          ...(finalWhereFilters?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: finalWhereFilters?.expectedDeliveryDate?.from?.$d,
                  to: finalWhereFilters?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: finalWhereFilters?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: filteredSort?.[0]
          ? {
              field: filteredSort?.[0]?.field,
              order: filteredSort?.[0]?.sort,
            }
          : initialNewDeliverySort,
      },
    });
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks, no-use-before-define
  const infiniteScrollRef = useInfiniteScroll(handleOrderScroll, isActive);

  const handleChallanDownload = async (
    id,
    windowReference,
    orderIds = null,
  ) => {
    setIsChallanLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    const response = await getOrderChallanDownloadUrl({
      variables: {
        where: {
          id,
        },
        ...(orderIds ? { orderIds } : {}),
      },
    });
    if (response?.data) {
      // eslint-disable-next-line no-param-reassign
      windowReference.location =
        response?.data?.getOrderChallanDownloadUrl?.challanUrl;
    }
    setIsChallanLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };

  const handleChallanReset = async (id) => {
    setIsResetLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    await resetOrderChallan({
      variables: {
        where: {
          id,
        },
      },
    });
    setIsResetLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };

  const handleStickerDownload = async (id, windowReference) => {
    setIsStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    const response = await getOrderStickerDownloadUrl({
      variables: {
        where: {
          id,
        },
      },
    });
    if (response?.data) {
      // eslint-disable-next-line no-param-reassign
      windowReference.location =
        response?.data?.getOrderStickerDownloadUrl?.stickerUrl;
    }
    setIsStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };

  const handleStickerReset = async (id) => {
    setIsResetStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: true,
    }));
    await resetOrderItemSticker({
      variables: {
        where: {
          id,
        },
      },
    });
    setIsResetStickerLoading((prevLoadingState) => ({
      ...prevLoadingState,
      [id]: false,
    }));
  };

  const getFilterData = (confirm) => {
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          search: orderSearchTerm,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          orderIds: whereFilter?.orderNumber,
          deliveryType: whereFilter?.deliveryType,
          status:
            whereFilter?.status?.length > 0
              ? whereFilter?.status
              : statusFilter,
          orderTypes:
            whereFilter?.orderTypes?.length > 0
              ? whereFilter?.orderTypes
              : undefined,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: whereFilter?.deliveredOn?.from?.$d,
            to: whereFilter?.deliveredOn?.to?.$d,
          },
          ...(whereFilter?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: whereFilter?.expectedDeliveryDate?.from?.$d,
                  to: whereFilter?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: whereFilter?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    finalWhereFiltersRef.current = {
      ...finalWhereFiltersRef?.current,
      ...whereFilter,
    };
    setExportFilter({ ...exportFilter, ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    paginationPropRef.current = { ...paginationProp, skip: 0, current: 1 };
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    let whereFiltersCopy;
    if (dataIndex === 'deliveryType') {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: null,
      };
    } else {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: [],
      };
    }
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    finalWhereFiltersRef.current = {
      ...finalWhereFiltersRef?.current,
      ...whereFiltersCopy,
    };
    setExportFilter({
      ...exportFilter,
      ...finalWhereFilters,
      ...whereFiltersCopy,
    });
    setIsOrderLoading(true);
    setNestedData({});
    setExpandedRowKeys([]);
    orders({
      variables: {
        filter: {
          ...initialNewDeliveryFilter,
          search: orderSearchTerm,
          limit: paginationProp?.pageSize || LIMIT,
          orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
          orderIds: whereFiltersCopy?.orderNumber,
          deliveryType: whereFiltersCopy?.deliveryType,
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : statusFilter,
          orderTypes:
            whereFiltersCopy?.orderTypes?.length > 0
              ? whereFiltersCopy?.orderTypes
              : undefined,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          deliveredOn: {
            from: whereFiltersCopy?.deliveredOn?.from?.$d,
            to: whereFiltersCopy?.deliveredOn?.to?.$d,
          },
          ...(whereFiltersCopy?.expectedDeliveryDate?.from
            ? {
                expectedDeliveryDate: {
                  from: whereFiltersCopy?.expectedDeliveryDate?.from?.$d,
                  to: whereFiltersCopy?.expectedDeliveryDate?.to?.$d,
                },
              }
            : {}),
          pincodeIds: whereFiltersCopy?.pincodes,
          statusTiming: finalWhereFilters?.statusTiming,
          isPreOrder: finalWhereFilters?.isPreOrder,
          isDelayed: finalWhereFilters?.isDelayed,
          orderTagIds:
            selectedTagIdsRef?.current?.length > 0
              ? selectedTagIdsRef.current
              : undefined,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialNewDeliverySort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    paginationPropRef.current = { ...paginationProp, skip: 0, current: 1 };
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      setFilterIsEnd(false);
      const optionsCopy = [];
      const orderStatus = isNewPath
        ? map(
            filter(
              ORDER_STATUS,
              (item) =>
                item?.value === ORDER_STATUS?.PLACED?.value ||
                item?.value === ORDER_STATUS?.CONFIRMED?.value,
            ),
            (item) => item,
          )
        : ORDER_STATUS;

      switch (filterIndex) {
        case 'deliveryPartnerName':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.DELIVERY?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'users':
          users({
            variables: {
              filter: {
                ...initialTableFilter,
                roles: [USER_ROLES?.USER?.key],
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'pincodes':
          pincodes({
            variables: {
              filter: {
                ...initialTableFilter,
              },
              sort: {
                field: 'pincode',
                order: 'ASC',
              },
            },
          });
          break;

        case 'deliveryType':
          forEach(DELIVERY_TYPE_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'orderNumber':
          orderSelect({
            variables: {
              filter: {
                ...initialTableFilter,
                orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
              },
              sort: initialTableSort,
            },
          });
          break;

        case 'status':
          forEach(orderStatus, (item) => {
            optionsCopy?.push({ label: item?.text, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'orderTypes':
          forEach(ORDER_TYPE_FILTER_OPTIONS, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
      setFilterSearch('');
    }
  }, [filterVisible]);

  const onFilterScroll = (event, dataIndex) => {
    if (dataIndex === 'status' || dataIndex === 'orderTypes') {
      return;
    }
    if (filterScrollDebounceJob) {
      filterScrollDebounceJob?.cancel();
      filterScrollDebounceJob = null;
    }
    if (
      !isUsersLoading &&
      !isPincodesLoading &&
      !isOrderSelectLoading &&
      !filterIsEnd
    ) {
      const target = event?.target;
      const currentLength = filterList?.length;
      if (
        target?.scrollTop + target?.offsetHeight >=
        target?.scrollHeight - 5
      ) {
        filterScrollDebounceJob = debounce(() => {
          setFilterLoading(true);
          switch (dataIndex) {
            case 'deliveryPartnerName':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.DELIVERY?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'users':
              users({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    roles: [USER_ROLES?.USER?.key],
                  },
                  sort: initialTableSort,
                },
              });
              break;

            case 'pincodes':
              pincodes({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                  },
                  sort: {
                    field: 'pincode',
                    order: 'ASC',
                  },
                },
              });
              break;

            case 'orderNumber':
              orderSelect({
                variables: {
                  filter: {
                    limit: LIMIT,
                    skip: currentLength,
                    search: filterSearch,
                    orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
                  },
                  sort: initialTableSort,
                },
              });
              break;

            default:
              break;
          }
        }, 500);
        filterScrollDebounceJob();
      }
    }
  };

  const handleFilterSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setFilterIsEnd(false);
    setFilterLoading(true);
    setFilterList([]);
    switch (dataIndex) {
      case 'deliveryPartnerName':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.DELIVERY?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'users':
        users({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              roles: [USER_ROLES?.USER?.key],
            },
            sort: initialTableSort,
          },
        });
        break;

      case 'pincodes':
        pincodes({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
            },
            sort: {
              field: 'pincode',
              order: 'ASC',
            },
          },
        });
        break;

      case 'orderNumber':
        orderSelect({
          variables: {
            filter: {
              ...initialTableFilter,
              limit: LIMIT,
              search: value,
              orderType: isNewPath ? ORDER_TYPE?.NEW : ORDER_TYPE?.ALL,
            },
            sort: initialTableSort,
          },
        });
        break;

      default:
        break;
    }
  };

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: value,
    };
    if (!value?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex, showSearch = true) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (
        dataIndex === 'createdAt' ||
        dataIndex === 'deliveredOn' ||
        dataIndex === 'expectedDeliveryDate'
      ) {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'deliveryType') {
        return (
          <div className="custom-filter-dropdown">
            <Group
              options={DELIVERY_TYPE_OPTIONS}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {showSearch && (
              <SearchComponent
                className="filter-search"
                id="search-container-id-roles"
                placeholder="Search..."
                name={dataIndex}
                query={filterSearch}
                setQuery={setFilterSearch}
                getData={(value) => handleFilterSearch(value, dataIndex)}
              />
            )}
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div
              className="filter-checkboxes"
              onScroll={(e) => onFilterScroll(e, dataIndex)}
            >
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (isVisible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(isVisible);
    },
  });

  const refreshAfterStaffSync = async () => {
    fetchOrders();
    const expandedIds = [...expandedRowKeys];
    setNestedData((prev) =>
      Object.fromEntries(
        Object.entries(prev || {})?.filter(([id]) => expandedIds?.includes(id)),
      ),
    );
    await Promise.all(
      expandedIds?.map(async (id) => {
        const res = await orderItems({ variables: { where: { id } } });
        if (res?.data) {
          setNestedData((state) => ({ ...state, [id]: res?.data }));
        }
      }),
    );
  };

  useEffect(() => {
    if (staffSyncKey) refreshAfterStaffSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffSyncKey]);

  const handleExpand = async (expanded, record) => {
    setIsLoading({
      [record?.id]: true,
    });
    setIsDisabled(true);
    if (!nestedData?.[record?.id]) {
      const res = await orderItems({
        variables: {
          where: {
            id: record?.id,
          },
        },
      });
      if (res?.data) {
        setNestedData((state) => ({
          ...state,
          [record?.id]: res?.data,
        }));
      }
      setIsDisabled(false);
    }
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record?.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys?.filter((key) => key !== record?.id));
    }
    setIsLoading({
      [record?.id]: false,
    });
    setIsDisabled(false);
  };

  const handleOrderChange = (value, record) => {
    setOrder(record);
    setIsOrderAction(true);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      setIsReadyPrompts(true);
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const handleOrderItemChange = (value, record, orderDetails) => {
    setOrderItem(record);
    setOrder(orderDetails);
    setIsOrderAction(false);
    if (value === FULFILLMENT_STATUS?.FULFILLED?.value) {
      if (isUnitsFeatureEnabled) {
        setIsUnitScanOpen(true);
      } else {
        setIsFulfillPrompts(true);
      }
    } else {
      setIsAddCommentModalOpen(true);
    }
  };

  const columns = [
    {
      title: 'ORDER ID',
      fixed: 'left',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      ellipsis: true,
      className: 'max-width-column',
      sorter: true,
      width: 150,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (_, record) => (
        <div className="d-flex align-center">
          {isNewPath && renderBlinkingDotEl(record?.status)}
          <RenderCopyIcon copyText={record?.orderNumber} />
          {record?.orderNumber || '-'}
          <>
            {record?.isExpressDelivery && !record?.isQuickDelivery && (
              <Tooltip
                title={EXPRESS_DELIVERY_TOOLTIP_TEXT}
                placement="top"
                zIndex={4}
              >
                <Tag color="red" className="ml-8 mr-0 express-delivery-icon">
                  {EXPRESS_DELIVERY_EMOJI}
                </Tag>
              </Tooltip>
            )}
            {record?.isQuickDelivery && (
              <Tooltip
                title={QUICK_DELIVERY_TOOLTIP_TEXT}
                placement="top"
                zIndex={4}
              >
                <Tag color="yellow" className="ml-8 mr-0 quick-delivery-icon">
                  {QUICK_DELIVERY_EMOJI}
                </Tag>
              </Tooltip>
            )}
          </>
          {record?.isFirstOrder && (
            <StatusFlag type={ORDER_TAG_TYPES.FIRST_ORDER} />
          )}
          {(record?.delayedInConfirmed || record?.delayedInShipped) && (
            <StatusFlag
              type={
                record?.delayedInShipped
                  ? ORDER_TAG_TYPES.DELAYED_IN_SHIPPED
                  : ORDER_TAG_TYPES.DELAYED_CONFIRMED
              }
            />
          )}
          {record?.metadata?.isContainBigToy && <BigToyIcon />}
          {hasPreOrderToy(record) && isNewPath && (
            <StatusFlag type={ORDER_TAG_TYPES.PRE_ORDER} />
          )}
          <OrderDynamicTags tags={record?.orderTags} />
        </div>
      ),
    },
    {
      title: 'ADLOGGS ORDER ID',
      dataIndex: 'partnerOrderId',
      key: 'partnerOrderId',
      align: 'left',
      ellipsis: true,
      className: 'max-width-column',
      width: 160,
      render: (_, record) => (
        <div className="d-flex align-center">
          <RenderCopyIcon
            copyText={record?.partnerOrderId}
            toolTipText="Copy Adloggs Order ID"
          />
          {record?.partnerOrderId || 'Not yet sent to Adloggs'}
        </div>
      ),
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      key: 'quantity',
      ellipsis: true,
      align: 'left',
      width: 100,
      className: 'max-width-column',
      render: (_, record) => record?.orderItemCount || '-',
    },
    {
      title: 'TOTAL ORDERS COUNT',
      dataIndex: 'totalOrdersCount',
      key: 'totalOrdersCount',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'totalOrdersCount' && sortedInfo?.order,
      render: (_, record) => record?.totalOrdersCount ?? '-',
    },

    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <>
          <Badge
            color={ORDER_STATUS?.[record?.status]?.color}
            text={ORDER_STATUS?.[record?.status]?.text}
          />
          {record?.isRetainedAfterProcess && (
            <Tooltip
              title="This order no longer matches the current filters. It will be hidden when you change filters, search, sort or page."
              placement="top"
            >
              <div>
                <Tag color="purple" className="mt-4 mr-0">
                  Processed
                </Tag>
              </div>
            </Tooltip>
          )}
        </>
      ),
      ...(!isNewPath ? filterPopup('status', false) : {}),
    },
    {
      title: 'ORDER TYPE',
      dataIndex: 'orderType',
      key: 'orderType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.orderType || '-',
      ...filterPopup('orderTypes', false),
    },

    {
      title: isNewPath ? 'ORDER PACKAGING SLA' : 'ORDER SLA',
      dataIndex: 'orderPackagingSLA',
      key: 'orderPackagingSLA',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        record?.nextStatusTime &&
        record?.status !== ORDER_STATUS.DELIVERED.value &&
        record?.status !== ORDER_STATUS.CANCELLED.value
          ? formatDate(record?.nextStatusTime, DATE_FORMATS?.WITH_TIME)
          : 'N/A',
    },
    {
      title: isNewPath ? 'ORDER PACKAGING TIME LEFT' : 'ORDER TIME LEFT',
      dataIndex: 'timeLeft',
      key: 'timeLeft',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const nextStatusTime = new Date(record?.nextStatusTime)?.getTime();
        return (
          <div>
            {!isEmpty(record?.nextStatusTime) &&
            (!isNewPath
              ? record?.status !== ORDER_STATUS?.DELIVERED?.value
              : true) &&
            record?.status !== ORDER_STATUS.DELIVERED.value &&
            record?.status !== ORDER_STATUS.CANCELLED.value ? (
              <Countdown date={nextStatusTime} renderer={renderer} />
            ) : (
              'N/A'
            )}
          </div>
        );
      },
    },

    {
      title: 'DELIVERY TYPE',
      dataIndex: 'deliveryType',
      key: 'deliveryType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) =>
        DELIVERY_TYPE?.[record?.deliveryType]?.label || '-',
      ...filterPopup('deliveryType'),
    },

    {
      title: 'USER NAME',
      dataIndex: 'userId',
      key: 'userId',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const userData = record?.user;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.pincode?.pincode || '-',
      ...filterPopup('pincodes'),
    },
    {
      title: 'DELAY REASON',
      dataIndex: 'delayReason',
      key: 'delayReason',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div className="action-button" onClick={(e) => e?.stopPropagation()}>
          {!isEmpty(record?.metadata?.delayReason) ? (
            <Tooltip title="View reason" placement="top" zIndex={4}>
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  setIsReasonPrompt(true);
                  setOrder(record);
                  setIsOrderAction(true);
                }}
              />
            </Tooltip>
          ) : (
            <span className="mr-24">-</span>
          )}
        </div>
      ),
    },
    {
      title: 'DELIVERY PARTNER NAME',
      dataIndex: 'deliveryPartnerName',
      key: 'deliveryPartnerName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const deliveryPartnerData = record?.deliveryPartner;
        const deliveryStaffDetails = record?.metadata?.deliveryStaffDetails;

        const partnerData = deliveryPartnerData || deliveryStaffDetails;

        return (
          <>
            {partnerData?.firstName ||
            partnerData?.lastName ||
            partnerData?.name ? (
              <div className="table-data-with-id">{`${
                partnerData?.firstName || partnerData?.name || ''
              } ${partnerData?.lastName || ''}`}</div>
            ) : (
              '-'
            )}
            {partnerData?.userNumber || partnerData?.phone ? (
              <div className="font-size-12">
                ({partnerData?.userNumber || partnerData?.phone})
              </div>
            ) : (
              ''
            )}
          </>
        );
      },
    },

    {
      title: 'PLACED ON',
      dataIndex: 'placedOn',
      key: 'placedOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.WITH_TIME),
      ...filterPopup('createdAt'),
    },
    {
      title: 'EXPECTED DELIVERY DATE',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'expectedDeliveryDate' && sortedInfo?.order,
      render: (_, record) =>
        formatDate(record?.expectedDeliveryDate, DATE_FORMATS?.WITH_TIME) ||
        '-',
      ...filterPopup('expectedDeliveryDate'),
    },
    {
      title: isNewPath ? 'ESTIMATED SHIPPING DATE' : 'DELIVERED ON',
      dataIndex: 'deliveredOn',
      key: 'deliveredOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.deliveredOn, DATE_FORMATS?.WITH_TIME) || '-',
      ...filterPopup('deliveredOn'),
    },
    {
      title: 'SHIPPED ATTACHMENTS',
      dataIndex: 'shippedAttachments',
      key: 'shippedAttachments',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const shippedAttachments = record?.deliveryAttachments?.filter(
          (attachment) =>
            attachment?.deliveryAttachmentsType ===
            ORDER_STATUS?.SHIPPED?.value,
        );

        const hasAttachments = shippedAttachments?.length > 0;
        const additionalCount = shippedAttachments?.length - 1;

        return (
          <span>
            {hasAttachments ? (
              <Image
                preview={{ visible: false }}
                width={20}
                height={20}
                src={getAttachmentSource(shippedAttachments?.[0])}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setToyImages(shippedAttachments);
                }}
                className="pointer"
              />
            ) : (
              <span>No Images</span>
            )}
            {additionalCount > 0 && ` + ${additionalCount}`}
          </span>
        );
      },
    },
    {
      title: 'DELIVERED ATTACHMENTS',
      dataIndex: 'deliveredAttachments',
      key: 'deliveredAttachments',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const deliveredAttachments = record?.deliveryAttachments?.filter(
          (attachment) =>
            [ORDER_STATUS?.DELIVERED?.value, null]?.includes(
              attachment?.deliveryAttachmentsType,
            ),
        );

        const hasAttachments = deliveredAttachments?.length > 0;
        const additionalCount = deliveredAttachments?.length - 1;

        return (
          <span>
            {hasAttachments ? (
              <Image
                preview={{ visible: false }}
                width={20}
                height={20}
                src={getAttachmentSource(deliveredAttachments?.[0])}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setToyImages(deliveredAttachments);
                }}
                className="pointer"
              />
            ) : (
              <span>No Images</span>
            )}
            {additionalCount > 0 && ` + ${additionalCount}`}
          </span>
        );
      },
    },
    {
      title: 'NOTE',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div onClick={(e) => e?.stopPropagation()}>
          {record?.cancellationData?.type ===
            CANCELLATION_TYPE?.LIBRARY?.value ||
          (record?.status === ORDER_STATUS?.NOT_DELIVERED?.value &&
            record?.metadata?.notDeliveredReason) ? (
            <div className="action-button">
              <Tooltip title="View comment" placement="top">
                <CommentIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setIsNotePrompts(true);
                    setOrder(record);
                    setIsOrderAction(true);
                  }}
                />
              </Tooltip>
            </div>
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 250,
      render: (_, record) => {
        let selectedValue = null;
        if (
          record?.status === ORDER_STATUS?.CANCELLED?.value ||
          record?.status === ORDER_STATUS?.CONFIRMED?.value
        ) {
          if (record?.status === ORDER_STATUS?.CANCELLED?.value) {
            selectedValue = FULFILLMENT_STATUS?.UNFULFILLED?.value;
          } else {
            selectedValue = FULFILLMENT_STATUS?.FULFILLED?.value;
          }
        } else {
          selectedValue = null;
        }
        return (
          <div onClick={(e) => e?.stopPropagation()}>
            {isNewPath && (
              <>
                <SelectComponent
                  size="small"
                  className="full-width"
                  placeholder="Select Action"
                  showAction={['click']}
                  value={selectedValue}
                  showSearch={false}
                  disabled={
                    !!selectedValue ||
                    (record.transferOrderItemCount !== 0 &&
                      record.transferOrderItemCount !== null)
                  }
                  disableFulfill={DISABLE_ORDER}
                  record={record}
                  options={NEW_ORDER_TABLE_ACTIONS_OPTIONS}
                  onSelect={(value) => handleOrderChange(value, record)}
                  allowClear={false}
                />
              </>
            )}
            <div className="d-flex gap-4 align-center">
              {!isNewPath && (
                <SelectComponent
                  size="small"
                  className="full-width"
                  placeholder="Select Action"
                  showAction={['click']}
                  value={
                    record?.status === ORDER_STATUS?.CANCELLED?.value
                      ? record?.status
                      : null
                  }
                  showSearch={false}
                  options={LIBRARY_ALL_DELIVERY_TABLE_ACTIONS_OPTIONS}
                  onSelect={(value) => handleOrderChange(value, record)}
                  disabled={
                    record?.status === ORDER_STATUS?.CANCELLED?.value ||
                    record?.status === ORDER_STATUS?.DELIVERED?.value ||
                    record?.status === ORDER_STATUS?.SHIPPED?.value ||
                    record?.status === ORDER_STATUS?.NOT_DELIVERED?.value
                  }
                  allowClear={false}
                />
              )}

              {!isNewPath && record?.deliveryPartner?.id && (
                <Tooltip title="View Location" placement="top" zIndex={4}>
                  <LocationIcon
                    onClick={(e) => {
                      e?.stopPropagation();
                      const [lng, lat] = record?.latLong?.coordinates || [];
                      openGoogleMaps(lat, lng, record?.deliveryPartner?.id);
                    }}
                    className="ml-16 mt-4 pointer svg-icon"
                  />{' '}
                </Tooltip>
              )}

              {record?.metadata?.adloggsOrderTrackUrl && isTruckIconVisible && (
                <Tooltip title="Track Order" placement="top" zIndex={4}>
                  <TruckIcon
                    className="ml-16 pointer svg-icon"
                    onClick={() => {
                      setTrackingUrl(record?.metadata?.adloggsOrderTrackUrl);
                      setIsTrackingModalOpen(true);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'DOWNLOAD PDF',
      dataIndex: 'downloadPDF',
      key: 'downloadPDF',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div onClick={(e) => e?.stopPropagation()}>
          <div className="action-button">
            <div className="mr-16">
              <ChallanDownloadButton
                order={record}
                handleChallanDownload={handleChallanDownload}
                isChallanLoading={isChallanLoading}
              />
            </div>
            <Button
              type="primary"
              size="small"
              className="table-action-primary-btn"
              onClick={() => handleChallanReset(record?.id)}
              loading={isResetLoading?.[record?.id]}
              disabled={
                ![
                  ORDER_STATUS?.READY_TO_SHIP?.value,
                  ORDER_STATUS?.CONFIRMED?.value,
                ]?.includes(record?.status)
              }
            >
              Reset Challan
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: 'CLUB ORDER NAME',
      dataIndex: 'clubOrderName',
      key: 'clubOrderName',
      align: 'left',
      ellipsis: true,
      className: 'max-width-column',
      width: 260,
      render: (_, record) => record?.metadata?.clubOrderName || '-',
    },
  ]?.filter((item) => {
    if (
      [
        'downloadPDF',
        'shippedAttachments',
        'deliveredAttachments',
        'partnerOrderId',
      ]?.includes(item?.dataIndex) &&
      isNewPath
    ) {
      return item?.hidden;
    }

    if (isNewPath && isUnitsFeatureEnabled && item?.dataIndex === 'actions') {
      return item?.hidden;
    }

    return !item?.hidden;
  });

  const fulfilmentPossibilityColumns = [
    {
      title: 'ORDER ID',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'left',
      ellipsis: true,
      fixed: 'left',
      className: 'max-width-column',
      sorter: true,
      width: 150,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
      render: (_, record) => (
        <div className="d-flex align-center">
          {isNewPath && renderBlinkingDotEl(record?.order?.status)}
          <RenderCopyIcon copyText={record?.order?.orderNumber} />
          {record?.order?.orderNumber || '-'}
          <>
            {record?.order?.isExpressDelivery &&
              !record?.order?.isQuickDelivery && (
                <Tooltip
                  title={EXPRESS_DELIVERY_TOOLTIP_TEXT}
                  placement="top"
                  zIndex={4}
                >
                  <Tag color="red" className="ml-8 mr-0 express-delivery-icon">
                    {EXPRESS_DELIVERY_EMOJI}
                  </Tag>
                </Tooltip>
              )}
            {record?.order?.isQuickDelivery && (
              <Tooltip
                title={QUICK_DELIVERY_TOOLTIP_TEXT}
                placement="top"
                zIndex={4}
              >
                <Tag color="yellow" className="ml-8 mr-0 quick-delivery-icon">
                  {QUICK_DELIVERY_EMOJI}
                </Tag>
              </Tooltip>
            )}
          </>
          {record?.order?.isFirstOrder && (
            <StatusFlag type={ORDER_TAG_TYPES.FIRST_ORDER} />
          )}
          {(record?.order?.delayedInConfirmed ||
            record?.order?.delayedInShipped) && (
            <StatusFlag
              type={
                record?.order?.delayedInShipped
                  ? ORDER_TAG_TYPES.DELAYED_IN_SHIPPED
                  : ORDER_TAG_TYPES.DELAYED_CONFIRMED
              }
            />
          )}
          {record?.order?.metadata?.isContainBigToy && <BigToyIcon />}
          {record?.isPreOrder && isNewPath && (
            <StatusFlag type={ORDER_TAG_TYPES.PRE_ORDER} />
          )}
          <OrderDynamicTags tags={record?.order?.orderTags} />
        </div>
      ),
    },
    {
      title: 'PRODUCT NAME',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
      align: 'left',
      width: 250,
      fixed: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <>
          <div className="table-data-with-id d-flex align-center">
            {record?.toy?.name || '-'}
          </div>
          {record?.toy?.toyNumber && (
            <div className="font-size-12">({record?.toy?.toyNumber})</div>
          )}
        </>
      ),
    },
    {
      title: 'MAPPED UNIT',
      hidden: !isUnitsFeatureEnabled,
      dataIndex: 'inventoryUnits',
      key: 'inventoryUnits',
      width: 200,
      align: 'left',
      className: 'max-width-column',
      onCell: () => ({
        onClick: (e) => e?.stopPropagation(),
      }),
      render: (_, record) => <MappedUnitsCell units={record?.inventoryUnits} />,
    },
    {
      title: 'IMAGES',
      dataIndex: 'images',
      key: 'images',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const images = record?.toy?.images;
        return (
          <span>
            {images?.length > 0 ? (
              <Image
                preview={{
                  visible: false,
                }}
                width={20}
                height={20}
                src={images?.[0]?.url}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setToyImages(images);
                }}
                className="pointer"
              />
            ) : (
              <span>No Images</span>
            )}
            {images?.length > 1 ? ` + ${images?.length - 1}` : ''}
          </span>
        );
      },
    },
    {
      title: 'BRAND',
      dataIndex: 'brand',
      key: 'brand',
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.toy?.brand?.name || '-',
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      key: 'quantity',
      ellipsis: true,
      align: 'left',
      width: 100,
      className: 'max-width-column',
      render: (_, record) => record?.quantity || '-',
    },
    {
      title: 'STOCK AT BOOKING',
      dataIndex: 'stockAtBooking',
      key: 'stockAtBooking',
      align: 'left',
      width: 150,
      render: (_, record) => record?.stockAtBooking ?? '-',
    },
    {
      title: (
        <ColumnTitleWithInfo
          title="AVAILABLE FOR BOOKING"
          info={STOCK_COLUMN_TOOLTIPS.AVAILABLE_FOR_BOOKING}
        />
      ),
      dataIndex: 'availableStock',
      key: 'availableStock',
      align: 'left',
      width: 220,
      render: (_, record) => record?.libraryInventory?.availableStock ?? '-',
    },
    {
      title: (
        <ColumnTitleWithInfo
          title="PHYSICALLY AVAILABLE STOCK"
          info={STOCK_COLUMN_TOOLTIPS.PHYSICALLY_AVAILABLE_STOCK}
        />
      ),
      dataIndex: 'physicallyAvailableStock',
      key: 'physicallyAvailableStock',
      align: 'left',
      width: 260,
      render: (_, record) =>
        record?.libraryInventory?.physicallyAvailableStock ?? '-',
    },
    {
      title: (
        <ColumnTitleWithInfo
          title="BLOCKED UNITS"
          info={STOCK_COLUMN_TOOLTIPS.BLOCKED_UNITS}
        />
      ),
      dataIndex: 'pendingDispatchCount',
      key: 'pendingDispatchCount',
      align: 'left',
      width: 260,
      render: (_, record) =>
        record?.libraryInventory?.pendingDispatchCount ?? '-',
    },
    {
      title: 'PACKAGING MATERIAL',
      dataIndex: 'packagingMaterial',
      key: 'packagingMaterial',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <>
          {record?.toy?.bag ? (
            <div>
              <span className="mr-8">
                {record?.toy?.bag?.name} ({record?.toy?.bag?.size})
              </span>
              <Image
                preview={{ visible: false }}
                width={22}
                src={record?.toy?.bag?.image}
                onClick={(e) => {
                  e?.stopPropagation();
                  setPreviewVisible(true);
                  setToyPreviewImage(record?.toy?.bag?.image);
                }}
                className="pointer"
              />
            </div>
          ) : (
            '-'
          )}
        </>
      ),
    },
    {
      title: 'TOTAL ORDERS COUNT',
      dataIndex: 'totalOrdersCount',
      key: 'totalOrdersCount',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'totalOrdersCount' && sortedInfo?.order,
      render: (_, record) => record?.order?.totalOrdersCount ?? '-',
    },
    {
      title: 'ORDER STATUS',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      ellipsis: true,
      width: 180,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const orderStatus = record?.order?.status;
        if (!orderStatus) return '-';
        const config = ORDER_STATUS?.[orderStatus];
        return (
          <Badge
            color={config?.color}
            text={config?.text || humanizeEnum(orderStatus)}
          />
        );
      },
    },
    {
      title: 'ORDER TYPE',
      dataIndex: 'orderType',
      key: 'orderType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.order?.orderType || '-',
      ...filterPopup('orderTypes', false),
    },
    {
      title: 'ORDER ITEM STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 220,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const config =
          FULFILLMENT_STATUS?.[record?.status] ||
          ORDER_STATUS?.[record?.status];
        return config ? (
          <Badge color={config?.color} text={config?.text || config?.label} />
        ) : (
          '-'
        );
      },
    },
    {
      title: isNewPath ? 'ORDER PACKAGING SLA' : 'ORDER SLA',
      dataIndex: 'orderPackagingSLA',
      key: 'orderPackagingSLA',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        record?.order?.nextStatusTime &&
        record?.order?.status !== ORDER_STATUS.DELIVERED.value &&
        record?.order?.status !== ORDER_STATUS.CANCELLED.value
          ? formatDate(record?.order?.nextStatusTime, DATE_FORMATS?.WITH_TIME)
          : 'N/A',
    },
    {
      title: isNewPath ? 'ORDER PACKAGING TIME LEFT' : 'ORDER TIME LEFT',
      dataIndex: 'timeLeft',
      key: 'timeLeft',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const nextStatusTime = new Date(
          record?.order?.nextStatusTime,
        )?.getTime();
        return (
          <div>
            {!isEmpty(record?.order?.nextStatusTime) &&
            (!isNewPath
              ? record?.order?.status !== ORDER_STATUS?.DELIVERED?.value
              : true) &&
            record?.order?.status !== ORDER_STATUS.DELIVERED.value &&
            record?.order?.status !== ORDER_STATUS.CANCELLED.value ? (
              <Countdown date={nextStatusTime} renderer={renderer} />
            ) : (
              'N/A'
            )}
          </div>
        );
      },
    },
    {
      title: 'DELIVERY TYPE',
      dataIndex: 'deliveryType',
      key: 'deliveryType',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) =>
        DELIVERY_TYPE?.[record?.order?.deliveryType]?.label || '-',
      ...filterPopup('deliveryType'),
    },
    {
      title: 'USER NAME',
      dataIndex: 'userId',
      key: 'userId',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const userData = record?.order?.user;
        return (
          <>
            {userData?.firstName || userData?.lastName ? (
              <div className="table-data-with-id">{`${
                userData?.firstName ? userData?.firstName : ''
              } ${userData?.lastName ? userData?.lastName : ''}`}</div>
            ) : (
              '-'
            )}
            {userData?.userNumber ? (
              <div className="font-size-12">({userData?.userNumber})</div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'PINCODE',
      dataIndex: 'pincode',
      key: 'pincode',
      ellipsis: true,
      align: 'left',
      width: 150,
      className: 'max-width-column',
      render: (_, record) => record?.order?.pincode?.pincode || '-',
      ...filterPopup('pincodes'),
    },
    {
      title: 'DELAY REASON',
      dataIndex: 'delayReason',
      key: 'delayReason',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div className="action-button" onClick={(e) => e?.stopPropagation()}>
          {!isEmpty(record?.order?.metadata?.delayReason) ? (
            <Tooltip title="View reason" placement="top" zIndex={4}>
              <CommentIcon
                className="mr-16 pointer svg-icon"
                onClick={() => {
                  setIsReasonPrompt(true);
                  setOrder(record?.order);
                  setIsOrderAction(true);
                }}
              />
            </Tooltip>
          ) : (
            <span className="mr-24">-</span>
          )}
        </div>
      ),
    },
    {
      title: 'DELIVERY PARTNER NAME',
      dataIndex: 'deliveryPartnerName',
      key: 'deliveryPartnerName',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) => {
        const deliveryPartnerData = record?.order?.deliveryPartner;
        const deliveryStaffDetails =
          record?.order?.metadata?.deliveryStaffDetails;
        const partnerData = deliveryPartnerData || deliveryStaffDetails;
        return (
          <>
            {partnerData?.firstName ||
            partnerData?.lastName ||
            partnerData?.name ? (
              <div className="table-data-with-id">{`${
                partnerData?.firstName || partnerData?.name || ''
              } ${partnerData?.lastName || ''}`}</div>
            ) : (
              '-'
            )}
            {partnerData?.userNumber || partnerData?.phone ? (
              <div className="font-size-12">
                ({partnerData?.userNumber || partnerData?.phone})
              </div>
            ) : (
              ''
            )}
          </>
        );
      },
    },
    {
      title: 'PLACED ON',
      dataIndex: 'placedOn',
      key: 'placedOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.order?.createdAt, DATE_FORMATS?.WITH_TIME),
      ...filterPopup('createdAt'),
    },
    {
      title: 'EXPECTED DELIVERY DATE',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      sorter: true,
      sortOrder:
        sortedInfo?.columnKey === 'expectedDeliveryDate' && sortedInfo?.order,
      render: (_, record) =>
        formatDate(
          record?.order?.expectedDeliveryDate,
          DATE_FORMATS?.WITH_TIME,
        ) || '-',
      ...filterPopup('expectedDeliveryDate'),
    },
    {
      title: isNewPath ? 'ESTIMATED SHIPPING DATE' : 'DELIVERED ON',
      dataIndex: 'deliveredOn',
      key: 'deliveredOn',
      ellipsis: true,
      align: 'left',
      width: 200,
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.order?.deliveredOn, DATE_FORMATS?.WITH_TIME) || '-',
      ...filterPopup('deliveredOn'),
    },
    {
      title: 'SHIPPED ATTACHMENTS',
      dataIndex: 'shippedAttachments',
      key: 'shippedAttachments',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const shippedAttachments = record?.order?.deliveryAttachments?.filter(
          (attachment) =>
            attachment?.deliveryAttachmentsType ===
            ORDER_STATUS?.SHIPPED?.value,
        );
        const hasAttachments = shippedAttachments?.length > 0;
        const additionalCount = shippedAttachments?.length - 1;
        return (
          <span>
            {hasAttachments ? (
              <Image
                preview={{ visible: false }}
                width={20}
                height={20}
                src={getAttachmentSource(shippedAttachments?.[0])}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setToyImages(shippedAttachments);
                }}
                className="pointer"
              />
            ) : (
              <span>No Images</span>
            )}
            {additionalCount > 0 && ` + ${additionalCount}`}
          </span>
        );
      },
    },
    {
      title: 'DELIVERED ATTACHMENTS',
      dataIndex: 'deliveredAttachments',
      key: 'deliveredAttachments',
      ellipsis: true,
      width: 200,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const deliveredAttachments = record?.order?.deliveryAttachments?.filter(
          (attachment) =>
            [ORDER_STATUS?.DELIVERED?.value, null]?.includes(
              attachment?.deliveryAttachmentsType,
            ),
        );
        const hasAttachments = deliveredAttachments?.length > 0;
        const additionalCount = deliveredAttachments?.length - 1;
        return (
          <span>
            {hasAttachments ? (
              <Image
                preview={{ visible: false }}
                width={20}
                height={20}
                src={getAttachmentSource(deliveredAttachments?.[0])}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setToyImages(deliveredAttachments);
                }}
                className="pointer"
              />
            ) : (
              <span>No Images</span>
            )}
            {additionalCount > 0 && ` + ${additionalCount}`}
          </span>
        );
      },
    },
    {
      title: 'NOTE',
      dataIndex: 'note',
      key: 'note',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div onClick={(e) => e?.stopPropagation()}>
          {record?.order?.cancellationData?.type ===
          CANCELLATION_TYPE?.LIBRARY?.value ? (
            <div className="action-button">
              <Tooltip title="View comment" placement="top">
                <CommentIcon
                  className="mr-16 pointer svg-icon"
                  onClick={() => {
                    setIsNotePrompts(true);
                    setOrder(record?.order);
                    setIsOrderAction(true);
                  }}
                />
              </Tooltip>
            </div>
          ) : (
            '-'
          )}
        </div>
      ),
    },
    {
      title: 'FULFILMENT POSSIBILITY',
      dataIndex: 'fulfilmentPossibility',
      key: 'fulfilmentPossibility',
      align: 'left',
      width: 200,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedPossibilityItem(record);
            setPossibilityDrawerVisible(true);
          }}
        >
          View
        </Button>
      ),
    },
    ...getStaffColumns(undefined, !isUnitsFeatureEnabled),
    {
      title: 'ACTIONS',
      dataIndex: 'actions',
      key: 'actions',
      width: 250,
      render: (_, record) => {
        const options = [
          {
            label: 'Mark as fulfilled',
            value: 'MARK_AS_FULFILLED',
            disabled:
              record?.isPreOrder &&
              record?.libraryInventory?.availableStock <= 0,
          },
          { label: 'Mark as cancelled', value: 'MARK_AS_CANCELLED' },
        ];
        let selectValue = null;
        if (record?.order?.status === ORDER_STATUS?.CANCELLED?.value) {
          selectValue = 'MARK_AS_CANCELLED';
        } else if (record?.status === FULFILLMENT_STATUS?.FULFILLED?.value) {
          selectValue = 'MARK_AS_FULFILLED';
        }

        return (
          <div onClick={(e) => e?.stopPropagation()}>
            <SelectComponent
              size="small"
              className="full-width"
              placeholder="Select Action"
              showSearch={false}
              value={selectValue}
              disabled={
                record?.order?.status === ORDER_STATUS?.CONFIRMED?.value ||
                record?.order?.status === ORDER_STATUS?.CANCELLED?.value ||
                record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
                record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
                (record?.transferOrders?.length > 0 &&
                  record?.transferOrders?.[0]?.status !==
                    TRANSFER_ORDER_STATUS.RECEIVED.value)
              }
              record={record}
              options={options}
              onChange={(value) => {
                if (value === 'MARK_AS_CANCELLED') {
                  handleOrderChange('UNFULFILLED', record?.order);
                } else if (value === 'MARK_AS_FULFILLED') {
                  handleOrderItemChange('FULFILLED', record, record?.order);
                }
              }}
              allowClear={false}
            />
          </div>
        );
      },
    },
  ]?.filter((item) => !item?.hidden);

  const finalColumns = isFulfilmentPossibility
    ? fulfilmentPossibilityColumns
    : columns;

  const staffRowSelection = {
    selectedRowKeys: staffSelectedKeys,
    onChange: (keys, rows) => {
      setStaffSelectedKeys(keys);
      setStaffSelectedRows(rows);
    },
    fixed: true,
    preserveSelectedRowKeys: true,
    getCheckboxProps: (record) => ({
      disabled: !isReassignableOrderItem(record),
    }),
  };

  const getSelectedStaffOrderItems = async () =>
    isFulfilmentPossibility
      ? (staffSelectedRows || [])?.filter(Boolean)
      : getSelectedOrderItems();

  const handleBulkStaffAssigned = (orderItemIds, field, user) => {
    setOrderList((prev) =>
      patchOrderItemStaff(prev, orderItemIds, field, user),
    );
    setNestedData((prev) =>
      patchNestedOrderItemStaff(prev, 'orderItems', orderItemIds, field, user),
    );
    setStaffSelectedKeys([]);
    setStaffSelectedRows([]);
    clearItemSelection();
  };

  const tablePageSize = paginationProp?.pageSize || LIMIT;
  const hasRetainedExtraRows =
    orderList?.length > tablePageSize &&
    orderList?.some((item) => item?.isRetainedAfterProcess);

  const rowSelection = {
    selectedRowKeys: selectedOrders,
    onChange: (selectedRowKeys) => {
      setSelectedOrders(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: ![
        ORDER_STATUS?.READY_TO_SHIP?.value,
        ORDER_STATUS?.SHIPPED?.value,
        ORDER_STATUS?.CONFIRMED?.value,
        ORDER_STATUS?.DELIVERED?.value,
      ]?.includes(record?.status),
    }),
  };

  const getOrderRowSelection = () => {
    if (!isNewPath) return rowSelection;
    return isFulfilmentPossibility && isUnitsFeatureEnabled
      ? staffRowSelection
      : undefined;
  };

  const expandedRowRender = (data) => {
    const orderItemList = nestedData?.[data?.id]?.orderItems?.data;
    const nestedColumns = [
      {
        title: 'ORDER ID',
        dataIndex: 'orderNumber',
        key: 'orderNumber',
        ellipsis: true,
        align: 'left',
        fixed: 'left',
        className: 'max-width-column',
        width: 100,
        render: (_, record) => (
          <div className="d-flex align-center">
            <RenderCopyIcon copyText={record?.order?.orderNumber} />
            {record?.order?.orderNumber || '-'}
          </div>
        ),
      },
      {
        title: 'PRODUCT NAME',
        dataIndex: 'productName',
        key: 'productName',
        ellipsis: true,
        align: 'left',
        fixed: 'left',
        width: 250,
        className: 'max-width-column',
        render: (_, record) => (
          <>
            <div className="table-data-with-id d-flex align-center">
              {record?.toy?.name || '-'}
            </div>
            {record?.toy?.toyNumber && (
              <div className="font-size-12">({record?.toy?.toyNumber})</div>
            )}
          </>
        ),
      },
      {
        title: 'MAPPED UNIT',
        hidden: !isUnitsFeatureEnabled,
        dataIndex: 'inventoryUnits',
        key: 'inventoryUnits',
        width: 200,
        align: 'left',
        className: 'max-width-column',
        onCell: () => ({
          onClick: (e) => e?.stopPropagation(),
        }),
        render: (_, record) => (
          <MappedUnitsCell units={record?.inventoryUnits} />
        ),
      },
      {
        title: 'IMAGES',
        dataIndex: 'images',
        key: 'images',
        ellipsis: true,
        width: 100,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => {
          const images = record?.toy?.images;
          return (
            <span>
              {images?.length > 0 ? (
                <>
                  <Image
                    preview={{
                      visible: false,
                    }}
                    width={20}
                    height={20}
                    src={images?.[0]?.url}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setVisible(true);
                      setToyImages(images);
                    }}
                    className="pointer"
                  />
                </>
              ) : (
                <span>No Images</span>
              )}
              {images?.length > 1 ? ` + ${images?.length - 1}` : ''}
            </span>
          );
        },
      },
      {
        title: 'BRAND',
        dataIndex: 'brand',
        key: 'brand',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => record?.toy?.brand?.name || '-',
      },
      {
        title: 'STATUS',
        dataIndex: 'status',
        key: 'status',
        ellipsis: true,
        width: 180,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => {
          const config =
            FULFILLMENT_STATUS?.[record?.status] ||
            ORDER_STATUS?.[record?.status];
          if (!record?.status) return '-';
          return (
            <Badge
              color={config?.color}
              text={config?.text || humanizeEnum(record?.status)}
            />
          );
        },
      },
      {
        title: 'STOCK AT BOOKING',
        dataIndex: 'stockAtBooking',
        key: 'stockAtBooking',
        align: 'left',
        width: 150,
        render: (_, record) => record?.stockAtBooking ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="AVAILABLE FOR BOOKING"
            info={STOCK_COLUMN_TOOLTIPS.AVAILABLE_FOR_BOOKING}
          />
        ),
        dataIndex: 'availableStock',
        key: 'availableStock',
        align: 'left',
        width: 220,
        render: (_, record) => record?.libraryInventory?.availableStock ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="PHYSICALLY AVAILABLE STOCK"
            info={STOCK_COLUMN_TOOLTIPS.PHYSICALLY_AVAILABLE_STOCK}
          />
        ),
        dataIndex: 'physicallyAvailableStock',
        key: 'physicallyAvailableStock',
        align: 'left',
        width: 260,
        render: (_, record) =>
          record?.libraryInventory?.physicallyAvailableStock ?? '-',
      },
      {
        title: (
          <ColumnTitleWithInfo
            title="BLOCKED UNITS"
            info={STOCK_COLUMN_TOOLTIPS.BLOCKED_UNITS}
          />
        ),
        dataIndex: 'pendingDispatchCount',
        key: 'pendingDispatchCount',
        align: 'left',
        width: 260,
        render: (_, record) =>
          record?.libraryInventory?.pendingDispatchCount ?? '-',
      },
      {
        title: 'Shelf',
        dataIndex: 'rack',
        key: 'rack',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => record?.rack?.name || '-',
      },
      {
        title: 'PACKAGING MATERIAL',
        dataIndex: 'packagingMaterial',
        key: 'packagingMaterial',
        ellipsis: true,
        width: 250,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => (
          <>
            {record?.toy?.bag ? (
              <div>
                <span className="mr-8">
                  {record?.toy?.bag?.name} ({record?.toy?.bag?.size})
                </span>
                <Image
                  preview={{ visible: false }}
                  width={22}
                  src={record?.toy?.bag?.image}
                  onClick={(e) => {
                    e?.stopPropagation();
                    setPreviewVisible(true);
                    setToyPreviewImage(record?.toy?.bag?.image);
                  }}
                  className="pointer"
                />
              </div>
            ) : (
              '-'
            )}
          </>
        ),
      },
      {
        title: 'AGE GROUPS',
        dataIndex: 'ageGroups',
        key: 'ageGroups',
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.toy?.ageGroups?.map((ageGroup) =>
            ageGroup?.minAge > 18 ? (
              <Tag key={ageGroup?.id}>
                {Math.floor(ageGroup?.minAge / 12)} -{' '}
                {Math.floor(ageGroup?.maxAge / 12)} Years
              </Tag>
            ) : (
              <Tag key={ageGroup?.id}>
                {ageGroup?.minAge} - {ageGroup?.maxAge} Months
              </Tag>
            ),
          ),
      },
      {
        title: 'FULFILLMENT STATUS',
        dataIndex: 'fulfillmentStatus',
        key: 'fulfillmentStatus',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) =>
          record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
          record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ? (
            <Badge
              color={FULFILLMENT_STATUS?.[record?.status]?.color}
              text={FULFILLMENT_STATUS?.[record?.status]?.text}
            />
          ) : (
            '-'
          ),
      },
      {
        title: 'EXPECTED RETURN',
        dataIndex: 'returnDate',
        key: 'returnDate',
        ellipsis: true,
        align: 'left',
        width: 200,
        className: 'max-width-column',
        render: (_, record) =>
          formatDate(record?.returnDate, DATE_FORMATS?.REGULAR) || '-',
      },
      {
        title: 'NOTE',
        dataIndex: 'note',
        key: 'note',
        ellipsis: true,
        align: 'left',
        width: 300,
        className: 'max-width-column',
        render: (_, record) => (
          <div className="action-button" onClick={(e) => e?.stopPropagation()}>
            {record?.cancellationData?.type ===
            CANCELLATION_TYPE?.LIBRARY?.value ? (
              <Tooltip title="View comment" placement="top" zIndex={4}>
                <CommentIcon
                  className="mr-24 pointer svg-icon"
                  onClick={() => {
                    setIsNotePrompts(true);
                    setOrderItem(record);
                    setIsOrderAction(false);
                  }}
                />
              </Tooltip>
            ) : (
              <span className="mr-24">-</span>
            )}
          </div>
        ),
      },
      {
        title: 'FULFILMENT POSSIBILITY',
        dataIndex: 'fulfilmentPossibility',
        key: 'fulfilmentPossibility',
        align: 'left',
        width: 200,
        hidden: !isNewPath,
        render: (_, record) => (
          <Button
            type="primary"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPossibilityItem(record);
              setPossibilityDrawerVisible(true);
            }}
          >
            View
          </Button>
        ),
      },
      ...getStaffColumns(undefined, !isUnitsFeatureEnabled),
      {
        title: 'ACTIONS',
        dataIndex: 'actions',
        key: 'actions',
        width: 250,
        render: (_, record) => (
          <SelectComponent
            size="small"
            className="full-width"
            placeholder="Select Action"
            showSearch={false}
            value={
              record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
              record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value
                ? record?.status
                : null
            }
            disabled={
              record?.status === FULFILLMENT_STATUS?.FULFILLED?.value ||
              record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
              record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
              (record?.transferOrders?.length > 0 &&
                record?.transferOrders?.[0]?.status !==
                  TRANSFER_ORDER_STATUS.RECEIVED.value)
            }
            disableFulfill={DISABLE_ORDER_ITEM}
            record={record}
            options={NEW_ORDER_EXPANDED_TABLE_ACTIONS_OPTIONS}
            onChange={(value) => handleOrderItemChange(value, record, data)}
            allowClear={false}
          />
        ),
      },
      {
        title: 'ACTIONS',
        dataIndex: 'allActions',
        key: 'allActions',
        ellipsis: true,
        width: 400,
        align: 'left',
        className: 'max-width-column',
        render: (_, record) => (
          <>
            <div className="action-button">
              <div className="mr-16">
                <Button
                  type="primary"
                  size="small"
                  className="table-action-primary-btn"
                  onClick={() => {
                    // eslint-disable-next-line no-undef
                    const windowReference = window.open();
                    handleStickerDownload(record?.id, windowReference);
                  }}
                  loading={isStickerLoading?.[record?.id]}
                  disabled={
                    record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                    record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
                    ![
                      ORDER_STATUS?.CONFIRMED?.value,
                      ORDER_STATUS?.READY_TO_SHIP?.value,
                    ]?.includes(data?.status)
                  }
                >
                  Download Sticker
                </Button>
              </div>
              <div className="mr-16">
                <Button
                  type="primary"
                  size="small"
                  className="table-action-primary-btn"
                  onClick={() => handleStickerReset(record?.id)}
                  loading={isResetStickerLoading?.[record?.id]}
                  disabled={
                    record?.status === FULFILLMENT_STATUS?.UNFULFILLED?.value ||
                    record?.status === FULFILLMENT_STATUS?.CANCELLED?.value ||
                    ![
                      ORDER_STATUS?.CONFIRMED?.value,
                      ORDER_STATUS?.READY_TO_SHIP?.value,
                    ]?.includes(data?.status)
                  }
                >
                  Reset Sticker
                </Button>
              </div>
              <Button
                type="primary"
                size="small"
                className="table-action-primary-btn"
                onClick={() => {
                  setOrderItem(record);
                  setIsOrderAction(false);
                  setIsContentMasterModalOpen(true);
                }}
              >
                View Content Logs
              </Button>
            </div>
          </>
        ),
      },
    ]?.filter((item) => {
      if (isNewPath && item?.dataIndex === 'allActions') {
        return item?.hidden;
      }
      return !item?.hidden;
    });

    return (
      <CommonTable
        loading={isLoading[data?.id]}
        columns={nestedColumns}
        dataSource={orderItemList || []}
        pagination={false}
        rowKey={(record) => record?.id}
        className="nested-table colored-nested-table"
        rowSelection={
          isNewPath && isUnitsFeatureEnabled
            ? getNestedRowSelection(data, orderItemList)
            : undefined
        }
      />
    );
  };

  const locale = {
    emptyText: isEmptyOrderList ? '' : <span />,
  };

  const expandIcon = ({ expanded, onExpand, record }) =>
    expanded ? (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded disabled"
        aria-label="Expand row"
        aria-expanded="false"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    ) : (
      <button
        type="button"
        className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
        aria-label="Collapse row"
        aria-expanded="true"
        onClick={(e) => onExpand(record, e)}
        disabled={isDisabled}
      />
    );

  const handleCancel = () => {
    setIsContentMasterModalOpen(false);
    setOrderItem({});
  };

  const handleSelectAllMobile = (checked) => {
    if (checked) {
      setSelectedOrders((prev) => {
        const newIds = orderList
          ?.filter((record) =>
            [
              ORDER_STATUS?.READY_TO_SHIP?.value,
              ORDER_STATUS?.SHIPPED?.value,
              ORDER_STATUS?.CONFIRMED?.value,
              ORDER_STATUS?.DELIVERED?.value,
            ]?.includes(record?.status),
          )
          ?.map((data) => data?.id);

        return Array.from(new Set([...prev, ...newIds]));
      });
    } else {
      setSelectedOrders([]);
    }
  };
  const findSelectableOrders = () =>
    orderList?.filter((record) =>
      [
        ORDER_STATUS?.READY_TO_SHIP?.value,
        ORDER_STATUS?.SHIPPED?.value,
        ORDER_STATUS?.CONFIRMED?.value,
        ORDER_STATUS?.DELIVERED?.value,
      ]?.includes(record?.status),
    );
  const isSelectAllDisabled = () =>
    !orderList?.some((record) =>
      [
        ORDER_STATUS?.READY_TO_SHIP?.value,
        ORDER_STATUS?.SHIPPED?.value,
        ORDER_STATUS?.CONFIRMED?.value,
        ORDER_STATUS?.DELIVERED?.value,
      ]?.includes(record?.status),
    );

  return (
    <>
      <ContentMasterModal
        isModalOpen={isContentMasterModalOpen}
        contentHistory={orderItem?.contentHistory}
        handleCancel={handleCancel}
      />
      <TrackOrderIframeModal
        isTrackingModalOpen={isTrackingModalOpen}
        setIsTrackingModalOpen={setIsTrackingModalOpen}
        trackingUrl={trackingUrl}
      />
      <DelayReasonModal
        note={
          !isEmpty(order?.metadata?.delayReason)
            ? order?.metadata
            : tempComment?.current?.[order?.id]
        }
        isModalOpen={isReasonPrompt}
        setIsModalOpen={setIsReasonPrompt}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
      />
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isFulfillPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsFulfillPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            setIsFulfillPrompts(false);
            handleFulfillOrderItem();
          }
        }}
        onCancel={() => setIsFulfillPrompts(false)}
        okButtonProps={{ loading: isFulfillLoading }}
      >
        Are you sure you want to
        <strong> Mark as fulfill </strong>
        this order item?
      </Modal>
      <Modal
        title="Caution"
        okText="Yes"
        cancelText="No"
        open={isReadyPrompts}
        onOk={() => {
          if (
            order?.delayedInConfirmed &&
            isEmpty(order?.metadata?.delayReason)
          ) {
            setIsReadyPrompts(false);
            setIsDelayReasonPrompts(true);
          } else {
            handleReadyOrder();
            setIsReadyPrompts(false);
          }
        }}
        onCancel={() => setIsReadyPrompts(false)}
        okButtonProps={{ loading: isReadyLoading }}
      >
        Are you sure you want to
        <strong> Mark as ready </strong>
        this order?
      </Modal>
      <UnitScanModal
        open={isUnitScanOpen}
        mode={UNIT_SCAN_MODES?.PICK}
        orderItemId={orderItem?.id}
        toyName={orderItem?.toy?.name}
        onClose={() => setIsUnitScanOpen(false)}
        onSuccess={(data) => {
          if (!data?.isOrderItemFulfilled) return;
          if (!isAllDeliveryTab) {
            fetchOrders();
            return;
          }
          const isOrderProcessed = isLastItem(order?.id);
          updateNestedDataLocally(
            order?.id,
            orderItem?.id,
            FULFILLMENT_STATUS?.FULFILLED?.value,
          );
          if (isOrderProcessed) refreshOrderInPlace(order?.id);
        }}
      />
      <AddCommentModal
        form={form}
        isOrderAction={isOrderAction}
        isModalOpen={isAddCommentModalOpen}
        setIsModalOpen={setIsAddCommentModalOpen}
        isBtnLoading={isAddCommentLoading}
        onFinish={
          isOrderAction ? handleAddCommentInOrder : handleAddCommentInOrderItem
        }
      />
      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        images={toyImages}
        setImages={setToyImages}
      />
      <CommonPreview
        visible={previewVisible}
        setVisible={setPreviewVisible}
        image={toyPreviewImage}
        setImage={setToyPreviewImage}
      />
      <NoteModal
        note={
          isOrderAction ? order?.cancellationData : orderItem?.cancellationData
        }
        isModalOpen={isNotePrompts}
        setIsModalOpen={setIsNotePrompts}
        isOrderAction={isOrderAction}
        adminNote={
          isOrderAction ? order?.comment?.comment : orderItem?.comment?.comment
        }
        order={order}
      />
      <FulfilmentPossibilityDrawer
        visible={possibilityDrawerVisible}
        onClose={() => setPossibilityDrawerVisible(false)}
        libraryInventoryId={selectedPossibilityItem?.libraryInventory?.id}
        physicallyAvailableStock={
          selectedPossibilityItem?.libraryInventory?.physicallyAvailableStock
        }
        isLibrary
        isDesktop={isDesktop}
      />
      <div className="d-flex justify-end align-center mb-16">
        {isDesktop && isNewPath && isUnitsFeatureEnabled && (
          <BulkAssignStaffButton
            isLibrary
            hasSelection={staffSelectedKeys?.length > 0 || hasItemSelection}
            getOrderItems={getSelectedStaffOrderItems}
            onSuccess={handleBulkStaffAssigned}
          />
        )}
        {isDesktop && (
          <>
            {!isFulfilmentPossibility && (
              <ShowPreOrderSwitch
                finalWhereFiltersRef={finalWhereFiltersRef}
                finalWhereFilters={finalWhereFilters}
                setFinalWhereFilters={setFinalWhereFilters}
                setPaginationProp={setPaginationProp}
                paginationPropRef={paginationPropRef}
                setOrderList={setOrderList}
                fetchOrders={fetchOrders}
                paginationProp={paginationProp}
                setIsOrderLoading={setIsOrderLoading}
                setSortedInfo={setSortedInfo}
                sortedInfoRef={sortedInfoRef}
                sortedInfo={sortedInfo}
              />
            )}
            <ShowDelayedOrdersSwitch
              finalWhereFiltersRef={finalWhereFiltersRef}
              finalWhereFilters={finalWhereFilters}
              setFinalWhereFilters={setFinalWhereFilters}
              setPaginationProp={setPaginationProp}
              paginationPropRef={paginationPropRef}
              setOrderList={setOrderList}
              fetchOrders={fetchOrders}
              paginationProp={paginationProp}
              setIsOrderLoading={setIsOrderLoading}
            />
          </>
        )}
        <OrderStatusSelect
          finalWhereFiltersRef={finalWhereFiltersRef}
          finalWhereFilters={finalWhereFilters}
          setFinalWhereFilters={setFinalWhereFilters}
          fetchOrders={() => {
            setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
            paginationPropRef.current = {
              ...paginationPropRef?.current,
              current: 1,
              skip: 0,
            };
            setOrderList([]);
            fetchOrders();
          }}
          setIsOrderLoading={setIsOrderLoading}
        />
        <OrderTagsFilter
          value={selectedTagIds}
          onChange={handleTagFilterChange}
          placeholder="Filter by Tags"
          className="ml-8"
        />
        <SearchComponent
          className={isDesktop ? 'search-box ml-16' : ''}
          query={orderSearchTerm}
          setQuery={setOrderSearchTerm}
          orderSearchTermRef={orderSearchTermRef}
          getData={handleSearch}
        />
      </div>
      {isDelayReasonPrompts && (
        <AddDelayReasonModal
          form={delayForm}
          isOrderAction={isOrderAction}
          isModalOpen={isDelayReasonPrompts}
          setIsModalOpen={setIsDelayReasonPrompts}
          isBtnLoading={isReadyLoading}
          onFinish={handleAddDelayReason}
        />
      )}
      {!isDesktop && (
        <div className="d-flex justify-end align-center">
          {!isNewPath && (
            <Checkbox
              indeterminate={
                selectedOrders?.length < findSelectableOrders()?.length &&
                selectedOrders?.length > 0
              }
              disabled={isSelectAllDisabled()}
              checked={
                selectedOrders?.length > 0 &&
                selectedOrders?.length === findSelectableOrders()?.length &&
                !isOrderLoading &&
                !isFetchMoreOrderLoading
              }
              onChange={(e) => {
                handleSelectAllMobile(e?.target?.checked);
              }}
              className="ml-16"
            >
              Select all
            </Checkbox>
          )}

          {!isFulfilmentPossibility && (
            <ShowPreOrderSwitch
              finalWhereFiltersRef={finalWhereFiltersRef}
              finalWhereFilters={finalWhereFilters}
              setFinalWhereFilters={setFinalWhereFilters}
              setPaginationProp={setPaginationProp}
              paginationPropRef={paginationPropRef}
              setOrderList={setOrderList}
              fetchOrders={fetchOrders}
              paginationProp={paginationProp}
              setIsOrderLoading={setIsOrderLoading}
              setSortedInfo={setSortedInfo}
              sortedInfoRef={sortedInfoRef}
              sortedInfo={sortedInfo}
            />
          )}
          <ShowDelayedOrdersSwitch
            finalWhereFiltersRef={finalWhereFiltersRef}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            setPaginationProp={setPaginationProp}
            paginationPropRef={paginationPropRef}
            setOrderList={setOrderList}
            fetchOrders={fetchOrders}
            paginationProp={paginationProp}
            setIsOrderLoading={setIsOrderLoading}
          />
          <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
            <FilterIcon
              className="mobile-svg-icon ml-16"
              onClick={() => setIsAllFilterModalOpen(true)}
            />
          </Badge>
          <div className="ml-16 d-flex align-center">
            <SortDropdown
              isOpen={isAllSortPopoverOpen}
              setIsOpen={setIsAllSortPopoverOpen}
              options={map(sort, (item) => item)}
              sortedInfo={sortedInfo}
              setSortedInfo={setSortedInfo}
              sortedInfoRef={sortedInfoRef}
              onFinish={handleSort}
            />
          </div>
        </div>
      )}
      {isDesktop ? (
        <div className="colored-table">
          <CommonTable
            rowClassName={(record) =>
              getRowClassName(
                isFulfilmentPossibility ? record?.order : record,
                thresholdData,
                playNotificationSound,
                criticalOrdersRef,
              )
            }
            locale={locale}
            columns={finalColumns}
            data={orderList || []}
            loading={isOrderLoading}
            onChange={
              hasRetainedExtraRows
                ? (_, tableFilter, sorter) =>
                    handleTableChange(
                      { ...paginationProp, current: 1 },
                      tableFilter,
                      sorter,
                    )
                : handleTableChange
            }
            paginationConfig={hasRetainedExtraRows ? null : paginationProp}
            rowKey={(record) => record?.id}
            expandedRowKeys={
              isFulfilmentPossibility ? undefined : expandedRowKeys
            }
            onExpand={
              isFulfilmentPossibility
                ? undefined
                : (_, record) => setOrder(record)
            }
            onRow={
              isFulfilmentPossibility
                ? undefined
                : () => ({
                    style: { cursor: 'pointer' },
                  })
            }
            expandable={
              isFulfilmentPossibility
                ? undefined
                : {
                    onExpand: (expanded, record) =>
                      handleExpand(expanded, record),
                    expandedRowRender: (record) => expandedRowRender(record),
                    expandIcon,
                    expandRowByClick: true,
                  }
            }
            rowSelection={getOrderRowSelection()}
          />
          {hasRetainedExtraRows && (
            <div className="d-flex justify-center" style={{ margin: '16px 0' }}>
              <Pagination
                current={paginationProp?.current}
                pageSize={tablePageSize}
                total={paginationProp?.total}
                pageSizeOptions={[10, 15, 20, 50]}
                showSizeChanger
                responsive
                onChange={(current, pageSize) =>
                  handleTableChange(
                    { ...paginationProp, current, pageSize },
                    null,
                    sortedInfoRef?.current,
                  )
                }
              />
            </div>
          )}
        </div>
      ) : (
        <div
          className={`${
            isNewPath
              ? 'mobile-container-search-action'
              : 'mobile-container-tab'
          } no-scroll-bar`}
        >
          <AllFilterOrderModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
            finalWhereFiltersRef={finalWhereFiltersRef}
            isNewPath={isNewPath}
          />
          <AllOrderItemModal
            order={order}
            isNewPath={isNewPath}
            setOrder={setOrder}
            isModalOpen={isAllOrderItemModalOpen}
            setIsModalOpen={setIsAllOrderItemModalOpen}
            handleExpand={handleExpand}
            nestedData={nestedData}
            isLoading={isLoading}
            setIsNotePrompts={setIsNotePrompts}
            setOrderItem={setOrderItem}
            setIsOrderAction={setIsOrderAction}
            handleOrderItemChange={handleOrderItemChange}
            handleStickerDownload={handleStickerDownload}
            isStickerLoading={isStickerLoading}
            handleStickerReset={handleStickerReset}
            isResetStickerLoading={isResetStickerLoading}
            setIsContentMasterModalOpen={setIsContentMasterModalOpen}
            setVisible={setVisible}
            setToyImages={setToyImages}
          />
          <AllOrderList
            orderList={orderList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreOrderLoading={isFetchMoreOrderLoading}
            isEmptyOrderList={isEmptyOrderList}
            isOrderLoading={isOrderLoading}
            setOrder={setOrder}
            isNewPath={isNewPath}
            setIsNotePrompts={setIsNotePrompts}
            setIsOrderAction={setIsOrderAction}
            handleOrderChange={handleOrderChange}
            setIsAllOrderItemModalOpen={setIsAllOrderItemModalOpen}
            handleChallanDownload={handleChallanDownload}
            isChallanLoading={isChallanLoading}
            handleChallanReset={handleChallanReset}
            isResetLoading={isResetLoading}
            setVisible={setVisible}
            setToyImages={setToyImages}
            handleSelectOrders={handleSelectOrders}
            selectedOrders={selectedOrders}
            setIsReasonPrompt={setIsReasonPrompt}
            thresholdData={thresholdData}
            playNotificationSound={playNotificationSound}
            criticalOrdersRef={criticalOrdersRef}
            renderBlinkingDotEl={renderBlinkingDotEl}
            showBlink={isNewPath}
            openGoogleMaps={openGoogleMaps}
            isAllDelivery={!isNewPath}
          />
        </div>
      )}
      <RiderLocationModal
        isLocationModalVisible={isLocationModalVisible}
        setIsLocationModalVisible={setIsLocationModalVisible}
        latitude={latitude}
        longitude={longitude}
      />
    </>
  );
};

export default AllDeliveryTable;
